/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential.
 *
 * Request items (v1_636). ONE record per thing the firm has asked, or will
 * ask, the client for. Everything the request-round rebuild does hangs off
 * this: the in-interview ask, the soft close, item-level reminders, the
 * client checklist, delegation and the waiting-on view.
 *
 * Why this exists: until v1_636 a requested item was a bare string in
 * collected_data.mlroRequestedQuestions. Strings carry no state, so a
 * regenerate rewrote the wording and lost what the client had already
 * done, reminders could only chase "the round", and nothing could say who
 * a case was waiting on. Items now carry a STABLE id derived from their
 * type and normalised wording, and a state machine, so the same item asked
 * in the interview, re-listed by Draft Client Request and answered by a
 * delegate is one record throughout.
 *
 * Storage: collected_data.requestItems (array). Pure functions only in this
 * module; no I/O and no Node-only imports, because the dashboard bundle
 * imports it too (api/_request-items.js re-exports it). Every function tolerates missing or malformed input and
 * never throws on shape.
 */
import { isNonPartyRole, applicantNamesFromBoard } from './verification-parties.js';
import { isProtectedEvidence, KEPT_OPEN_NOTE } from './protected-evidence.js';
import { clientGapLabel } from './client-copy.js';
import { ASSEMBLY_SECTIONS } from './board-sections.js';

export const ITEM_STATES = Object.freeze([
  'open',               // identified, not yet put to the client
  'asked_in_interview', // Alex asked it before submission
  'requested',          // sent in an officer request round
  'promised',           // client committed to a date (dueAt)
  'delegated',          // client handed it to a named third party (owner)
  'partial',            // something arrived, officer judged it incomplete
  'answered',           // client answered / document received, awaiting officer verdict
  'not_applicable',     // client claims N/A with a reason; officer must accept or reject
  'resolved',           // officer accepted as satisfied
  'withdrawn'           // officer withdrew the ask
]);

// States in which the client (or their delegate) still owes something.
export const OPEN_STATES = Object.freeze(['open', 'asked_in_interview', 'requested', 'promised', 'delegated', 'partial']);
// States that count as "the client has done their part" for reminder purposes.
export const CLIENT_DONE_STATES = Object.freeze(['answered', 'not_applicable', 'resolved', 'withdrawn']);

// v1_921: WHAT THE CLIENT SEES IS ONLY WHAT THE OFFICER SENT, AND STILL OWED.
// The portal showed a second list, "Outstanding documents", built from the
// analysis's document gaps (report.documentGaps) once any round had gone out:
// items the officer had not sent ("Required, not yet asked"), the same asks in
// other words, items the client had already answered, and items the officer can
// fetch from a public register (Fig Markets, 04/10/26). The client now sees one
// list: request items an officer request round sent (a 'requested' state in
// their history, or 'requested' now), still owed by the client (requested,
// promised, delegated, partial), with the same ask in two wordings shown once.
// Never shown: open or interview only items (never sent), answered or not
// applicable (with the officer), resolved (accepted) and withdrawn.
export const CLIENT_VISIBLE_STATES = Object.freeze(['requested', 'promised', 'delegated', 'partial']);
export function sentByOfficer(item) {
  if (!item) return false;
  if (item.state === 'requested') return true;
  return (Array.isArray(item.history) ? item.history : []).some(h => h && (h.to === 'requested' || h.from === 'requested'));
}
// v1_921 (owner, 04/10/2026): what a client route RETURNS carries no request
// item the officer has not sent. Before submission the interview's own card
// ("Before you submit") still needs the items Alex put to the client, so those
// stay; an item never put to the client (open, never asked) is never carried.
// After submission (submittedAt, or a request round sent) only items an officer
// round sent are carried. Applied to every client read of collected_data (the
// session read, the invitation load, the answer reconcile). The stored list is
// untouched, and update-session never lets a client write requestItems.
export function putToClient(item) {
  if (!item) return false;
  if (sentByOfficer(item) || item.askedAt) return true;
  return (Array.isArray(item.history) ? item.history : []).some(h => h && (h.to === 'asked_in_interview' || h.from === 'asked_in_interview'));
}
export function clientCarriedItems(items = [], cd = {}) {
  const submitted = !!(cd && (cd.submittedAt || cd.mlroRequestSentAt));
  return (Array.isArray(items) ? items : []).filter(i => i && (submitted ? sentByOfficer(i) : putToClient(i)));
}
export function clientSafeCollected(cd) {
  if (!cd || typeof cd !== 'object' || !Array.isArray(cd.requestItems)) return cd;
  return { ...cd, requestItems: clientCarriedItems(cd.requestItems, cd) };
}
export function clientChecklistItems(items = []) {
  const vis = (Array.isArray(items) ? items : []).filter(i => i && i.id && CLIENT_VISIBLE_STATES.includes(i.state) && sentByOfficer(i));
  const out = [];
  for (const i of vis) {
    const same = out.find(o => o.id === i.id || (labelHead(o.label) && labelHead(o.label) === labelHead(i.label)) || sameAskHead(o.label, i.label) || sameAskHead(i.label, o.label));
    if (same) {
      // One line for the same ask: the fuller wording, every id kept so the
      // client's action (date, delegate, not applicable, upload) reaches each.
      if (same.id !== i.id && !same.mergedIds.includes(i.id)) same.mergedIds.push(i.id);
      if (String(i.label || '').length > String(same.label || '').length) same.label = i.label;
      continue;
    }
    out.push({ ...i, mergedIds: [] });
  }
  return out;
}

export const THEMES = Object.freeze({
  ownership:  { label: 'Ownership and control',        order: 1 },
  identity:   { label: 'Identity and authority',       order: 2 },
  funds:      { label: 'Source of funds and wealth',   order: 3 },
  activity:   { label: 'Business activity and flows',  order: 4 },
  regulatory: { label: 'Regulatory status and policies', order: 5 },
  financials: { label: 'Financial information',        order: 6 },
  documents:  { label: 'Company documents',            order: 7 },
  other:      { label: 'Other points',                 order: 8 }
});

export function normaliseLabel(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[“”"'`]/g, '')
    .replace(/[^a-z0-9 ,.;:()/-]/g, '')
    .trim();
}

// The id is stable across regenerations as long as the item is the same ask.
// It hashes the type plus the head of the normalised wording (the "document
// needed" part, before the client-facing "because" clause), so a reworded
// reason does not create a duplicate item.
export function makeItemId(type, label) {
  const norm = normaliseLabel(label);
  const head = norm.split(/\b(because|as |since |so that)\b/)[0].trim().slice(0, 90);
  const str = `${type || 'question'}|${head}`;
  const fnv = (seed) => { let h = seed >>> 0; for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; } return h.toString(16).padStart(8, '0'); };
  return `ri_${(fnv(2166136261) + fnv(0x811c9dc5 ^ 0x5bd1e995)).slice(0, 12)}`;
}

const THEME_RULES = [
  ['ownership',  /\b(ubo|beneficial own|shareholder|register of members|structure chart|ownership|holding|parent company|group structure|control)\b/i],
  ['identity',   /\b(director|register of directors|authorised signatory|power of attorney|board resolution|authority to act|identity|passport|proof of address)\b/i],
  ['funds',      /\b(source of (funds|wealth)|bank statement|funding|capitalis|wealth|inherit|proceeds)\b/i],
  ['activity',   /\b(customer|counterpart|volume|turnover|transaction|geograph|jurisdiction|currency|currencies|business model|revenue|how (the )?(business|company) makes|activity|purpose)\b/i],
  ['regulatory', /\b(licen[cs]e|regulat|aml policy|aml\/ctf|compliance policy|mlro|supervis|registration number|vasp|emi|msb|fca|finma|vqf|sanctions screening|kyc procedure)\b/i],
  ['financials', /\b(audited accounts|financial statements|management accounts|annual accounts|balance sheet|accounts for)\b/i],
  ['documents',  /\b(certificate of incorporation|articles|memorandum|constitution|incorporation|registry extract|good standing|prospectus|ppm)\b/i]
];

export function classifyTheme(label) {
  const t = String(label || '');
  for (const [theme, re] of THEME_RULES) if (re.test(t)) return theme;
  return 'other';
}

// Required-for-tier heuristic. Deliberately conservative: an item is
// "required" when leaving it out would, on the methodology, prevent a
// decision at the provisional tier. The officer can override per item and
// the firm can tune the interview cap; this only decides what Alex asks
// before submission versus what waits for the soft close.
const REQUIRED_ANY_TIER = /\b(certificate of incorporation|register of (directors|members)|beneficial own|ubo|structure chart|source of funds|bank statement|purpose|intended (use|activity)|how (the )?(business|company) (makes|generates)|revenue|authority to act|board resolution|power of attorney)\b/i;
const REQUIRED_MEDIUM_UP = /\b(counterpart|geograph|jurisdiction|volume|turnover|currency|currencies|licen[cs]e|aml policy|aml\/ctf|registration number|customer)\b/i;
const REQUIRED_HIGH = /\b(source of wealth|audited accounts|financial statements|management accounts|ownership chain|nominee|trust deed|high[- ]risk)\b/i;

// The tier whose rules flag items required while no tier is in force (see buildCandidates).
export const PENDING_REQUIRED_TIER = 'High';

export function isRequiredForTier(label, tier) {
  const t = String(label || '');
  const tierN = String(tier || 'Medium').toLowerCase();
  if (REQUIRED_ANY_TIER.test(t)) return true;
  if (tierN !== 'low' && REQUIRED_MEDIUM_UP.test(t)) return true;
  if (tierN === 'high' && REQUIRED_HIGH.test(t)) return true;
  return false;
}

function nowIso() { return new Date().toISOString(); }

export function newItem({ type = 'question', label, why = '', source = 'officer', tier = 'Medium', round = 0, state = 'open', extra = {} }) {
  const clean = String(label || '').trim();
  if (!clean) return null;
  // Classify at source (v1_728): a document-shaped 'question' becomes 'document'
  // so the checklist offers Upload, and reconciliation and possible-match treat
  // it as a document downstream. Identity/email asks are never upgraded.
  const effType = inferAskType(clean, type, extra && extra.theme);
  const id = makeItemId(effType, clean);
  return {
    id,
    type: effType,
    label: clean,
    why: String(why || ''),
    theme: classifyTheme(clean),
    required: isRequiredForTier(clean, tier),
    source,
    state,
    owner: { kind: 'client' },
    dueAt: null,
    askedAt: null,
    answeredAt: null,
    lastActionAt: nowIso(),
    lastActionBy: 'system',
    round,
    note: '',
    evidence: null,
    history: [{ at: nowIso(), from: null, to: state, by: 'system' }],
    ...extra
  };
}

// A direct email address for every person whose identity must be verified is
// not tier-scaled: without one the compliance team cannot send that person
// their own secure verification link, so identity verification cannot start at
// ANY tier. This finds the natural persons on the board who need verifying
// (directors, beneficial owners, and anyone already listed for identity
// verification) and returns those who have no usable email anywhere on the
// file, so the pre-submission pass asks for it and it stays on the checklist
// until it arrives. Corporate owners and entity-shaped names are excluded — an
// entity has no personal verification link — and a person listed in more than
// one place with an email in only one is not flagged.
const VERIF_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ENTITY_NAME_RE = /\breg(?:\.|istration)?\s*(?:no|number)\b|\b(?:ltd|limited|llc|l\.l\.c|inc|incorporated|gmbh|ag|sa|s\.a|plc|holdings?|foundation|stiftung|anstalt|trust|corp|corporation|company|group|partners|capital|fund|sarl|s\.a\.r\.l|bv|nv|oy|ab|pte|pty)\b/i;
function isNaturalPerson(p) {
  if (!p || typeof p !== 'object') return false;
  if (p.corporateUBO === true) return false;
  const nm = String(p.name || '').trim();
  if (!nm) return false;
  if (ENTITY_NAME_RE.test(nm)) return false;
  return true;
}

export function verificationParties(board = {}) {
  const b = board && typeof board === 'object' ? board : {};
  const applicantNames = applicantNamesFromBoard(b);
  // normalised name -> { name, role, hasEmail }. Gather across every source so a
  // person on two lists with an email in one is counted as having an email.
  const people = new Map();
  const note = (p, kind) => {
    // Verification subjects only (v1_650): a natural person who is a party of the
    // applicant. A document-sourced non-party (an issuing-authority signatory, an
    // officer of another entity, a contingent future appointee, or a person whose
    // role attributes them to another named entity) must never generate a
    // "collect this person's email" request item — that inflated the
    // items-to-request count with phantom parties (Sage Capital, 16/09/26).
    if (!isNaturalPerson(p) || isNonPartyRole(p && p.role, applicantNames)) return;
    const key = normaliseLabel(p.name);
    if (!key) return;
    const cur = people.get(key) || { name: String(p.name).trim(), role: '', hasEmail: false };
    if (!cur.role) cur.role = String(p.role || kind || '').trim();
    if (VERIF_EMAIL_RE.test(String(p.email || '').trim())) cur.hasEmail = true;
    people.set(key, cur);
  };
  for (const d of (Array.isArray(b.directors) ? b.directors : [])) note(d, 'director');
  for (const u of (Array.isArray(b.ubos) ? b.ubos : [])) note(u, 'beneficial owner');
  for (const s of (Array.isArray(b.sumsubRequired) ? b.sumsubRequired : [])) note(s, 'to be verified');
  return [...people.values()].filter(p => !p.hasEmail);
}

// INTERNAL ACTIONS ARE NOT CLIENT DOCUMENT GAPS (v1_689). A document gap drives
// what the CLIENT is asked to provide, but some checks are the compliance team's
// own internal actions and must never reach the client's outstanding list —
// identity verification, sanctions/wallet screening, Travel Rule execution and
// Form A/K signatures are tracked in their own panels. Wallet OWNERSHIP proof
// belongs here too: for a SELF-CUSTODIED (private/unhosted) wallet it is proven
// by the platform's Satoshi test, and for an exchange-held wallet by the
// account-in-entity-name confirmation captured in the Travel Rule record —
// NEVER by asking a third party (an exchange or payment provider) to "confirm
// the address is a receiving address assigned to the company", which mislabels a
// self-custodied address the third party has no relationship to (first live run,
// Sage Capital UK). The generators are told this (df-process / interview
// prompts); this predicate is the deterministic backstop that strips any such
// gap that a model still emits, on legacy and live reports alike, before it
// becomes a client-facing item. The regex is shared with api/confirm-submission.
//
// v1_699: PERSON-IDENTITY documents are handled by Sumsub (tracked in
// sumsubRequired), never asked as a client upload. The model phrased identity as
// a "Photo identification and proof of address" document gap, which slipped the
// filter (it only matched "identity verification") and became a client-facing
// item (CompliantID Ltd 21/09/26). Now person-identity signals are caught —
// photo ID, proof of identity, passport, driving licence, national identity,
// identity document, and identity-verification (hyphen or space). "Proof of
// address" alone is deliberately NOT matched, so the legitimate ENTITY registered-
// office / proof-of-address ask is preserved; only person-identity asks are struck.
export const INTERNAL_GAP_RE = /sumsub|identity[\s-]verification|photo\s*id(?:entification)?\b|proof of identity|\bpassport\b|driv(?:ing|er.?s)\s+licen[cs]e|\bnational identity\b|\bnational id card\b|identity document|wallet ownership|wallet screening|chainalysis|travel rule|sanctions screening|compliance team|\bform a\b|\bform k\b|receiving address|proof of control|satoshi|(?:confirm|verif\w*|proof|ownership|control)[^.]{0,40}(?:wallet|crypto|blockchain|on-chain)\s*address|(?:wallet|crypto|blockchain|on-chain)\s*address[^.]{0,40}(?:belong|assigned|owned|control|confirm|verif)/i;
// v1_718: an internal ANALYSIS NOTE that leaked into documentGaps as a client ask.
// The generator sometimes prefixes a gap with the board FIELD it relates to and
// then writes an observation rather than an ask — "directors: The Companies House
// officer extract states…", "activityProfile: The order form corroborating…",
// "sowAssessment / sofAssessment (supporting docs on the order form): …". These
// are internal routing, never client-facing. Anchored on the known board field
// names (so a genuine ask, which never opens with a camelCase field token + colon,
// is safe), allowing an optional "/ field" chain and an optional "(parenthetical)".
// Walk wb0c3f810: the list lacked two of the sections the assembly check can
// name (objectAndPurpose, bankingProfile), so "objectAndPurpose: The AML
// framework is missing ..." reached the client's list. Every section the check
// reads (board-sections.js, the one list) is now on it by construction.
export const INTERNAL_NOTE_FIELDS = [...new Set(['activityProfile', 'companyDetails', 'sowAssessment', 'sofAssessment', 'entityProfile', 'regulatoryStatus', 'amlSupervision', 'amlPolicyAssessment', 'websiteFindings', 'vqfProfile', 'ownershipChain', 'documentsReceived', 'riskFactors', 'redFlags', 'taxCompliance', 'directors', 'ubos', 'authorisedSignatories', 'sumsubRequired', ...ASSEMBLY_SECTIONS])];
export const INTERNAL_NOTE_PREFIX_RE = new RegExp('^\\s*(' + INTERNAL_NOTE_FIELDS.join('|') + ')\\b\\s*(\\/\\s*[a-zA-Z]+\\s*)*(\\([^)]*\\))?\\s*:');
export function isInternalGap(g) {
  const label = typeof g === 'string' ? g : (g && (g.documentType || g.description || g.gap)) || '';
  const s = String(label);
  return INTERNAL_GAP_RE.test(s) || INTERNAL_NOTE_PREFIX_RE.test(s);
}

// v1_719: a documentGaps entry that asks for something that CANNOT EXIST YET — a
// FUTURE OBLIGATION ("accounts once the first period closes", "the first audit
// when it falls due") — is not a current client request. The generation prompt
// already forbids it, but the model still emits one; this is the deterministic
// backstop. Such an item is taken off the client send list and recorded as a
// dated follow-up to revisit after the stated event, never sent to the client now.
// Anchored on explicit future phrasings so ordinary asks ("a bank statement for
// the last three months", "the certificate of incorporation") are not swept up.
export const FUTURE_OBLIGATION_RE = /\bonce\b[^.]{0,40}\b(available|prepared|close[sd]?|end[sed]*|complete[d]?|ready|finalised|finalized)\b|\bonce the (first|initial|next)\b[^.]{0,20}\b(accounting )?(period|year|quarter|audit)\b|\bwhen\b[^.]{0,40}\b(becomes?|falls?|is)\b[^.]{0,15}\bdue\b|\bafter the (first|next|initial)\b[^.]{0,30}\b(period|year|quarter)\b[^.]{0,15}\b(close[sd]?|end[sed]*|is complete)\b|\bnot yet\b[^.]{0,25}\b(exist|due|available|close[sd]?|end[sed]*|prepared|filed|audited)\b|\bhas not (yet )?\b(close[sd]?|end[sed]*|been (prepared|filed|audited|finalised|finalized))\b|\bfor (a|the) (year|period) not yet (end[sed]*|close[sd]?)\b|\bwill not (yet )?exist\b/i;
export function isFutureObligation(g) {
  const label = typeof g === 'string' ? g : (g && (g.documentType || g.description || g.gap || g.label)) || '';
  return FUTURE_OBLIGATION_RE.test(String(label));
}
// Split a documentGaps list into the ones to KEEP on the client send list and the
// future obligations to DEFER (record + revisit later, never ask now). Pure.
export function partitionFutureObligations(documentGaps = []) {
  const gaps = Array.isArray(documentGaps) ? documentGaps : [];
  const kept = [], deferred = [];
  for (const g of gaps) { (isFutureObligation(g) ? deferred : kept).push(g); }
  return { kept, deferred };
}

// v1_720: a possible-match must be a document whose TYPE can actually answer the
// item's CONCERN. The reconcile matched an item to a received document by word
// overlap alone, so a source-of-wealth ask ("how the holding company funded the
// company") was matched to a Memorandum of Association — a constitutional document
// that cannot evidence funding (CompliantID Ltd). We classify the item's concern
// and the document's type and only NAME the document when the two are compatible;
// otherwise the generic "check the documents" hint (doc:null) stands.
const CONCERN_PATTERNS = [
  ['sourceOfFunds', /source of (funds|wealth)|how .{0,40}(funded|financed|capitalis|paid for)|where .{0,30}(money|funds|capital) (come|came|will come)|funding|capital\b|reserves|bank statement|proof of funds/i],
  ['ownership', /beneficial owner|\bUBO\b|ultimate(ly)? (benefici|own)|person(s)? with significant control|\bPSC\b|register of members|sharehold|share certificate|who (ultimately )?owns|owns? \d|structure chart|ownership/i],
  ['control', /register of (directors|officers)|officer (extract|filing|register)|\bdirector\b|authorised signator|incumbency|significant control/i],
  ['identity', /passport|driv(ing|er)|identity|proof of identity|photo id/i],
  ['address', /registered office|proof of address|utility bill|address (as )?filed/i],
  ['activity', /invoice|order form|subscription (agreement|order)|customer (contract|agreement)|sales|revenue|business activit|contract/i],
  ['regulatory', /licen[cs]e|permission|authoris|regulator|\bSRO\b|\bVQF\b|AML polic|registration certificate/i],
  ['financials', /accounts?\b|financial statement|balance sheet|management accounts|audited/i],
];
const DOCTYPE_PATTERNS = [
  ['constitutional', /memorandum|articles of association|\bM&A\b|constitution|statutes|bylaws|certificate of incorporation|incorporation/i],
  ['register', /register of (members|directors|officers)|\bPSC\b|shareholder (register|filing)|officer extract|incumbency|share certificate/i],
  ['structureChart', /structure chart|group structure|ownership chart|org(anisation)? chart/i],
  ['bank', /bank statement|account (confirmation|opening)|revolut|\bIBAN\b|statement of account/i],
  ['financials', /management accounts|financial statement|balance sheet|audited accounts|\baccounts\b|profit and loss|\bP&L\b/i],
  ['identity', /passport|driv(ing|er).{0,3}licen[cs]e|identity (document|card)|photo id/i],
  ['activityDoc', /order form|invoice|subscription (agreement|order)|customer (contract|agreement)|sales contract/i],
  ['licence', /licen[cs]e|permission|authoris|registration certificate|\bSRO\b|\bVQF\b membership/i],
  ['addressDoc', /utility bill|lease|tenancy|proof of address/i],
];
const classify = (patterns, s) => { const t = String(s || ''); for (const [k, re] of patterns) if (re.test(t)) return k; return null; };
export const concernOf = (label) => classify(CONCERN_PATTERNS, label);
export const docTypeOf = (name) => classify(DOCTYPE_PATTERNS, name);
// Which document types can plausibly satisfy each concern.
const CONCERN_DOCTYPES = {
  sourceOfFunds: ['bank', 'financials'],
  ownership: ['register', 'structureChart', 'constitutional'],
  control: ['register', 'constitutional'],
  identity: ['identity'],
  address: ['addressDoc', 'register', 'constitutional'],
  activity: ['activityDoc', 'financials'],
  regulatory: ['licence'],
  financials: ['financials', 'bank'],
};
// True unless BOTH the item's concern and the document's type are known AND the
// document type cannot satisfy that concern — i.e. only a confidently-incompatible
// pairing is rejected; anything we cannot classify falls back to the word match.
export function docMatchSatisfiesConcern(itemLabel, docName) {
  const concern = concernOf(itemLabel);
  const dt = docTypeOf(docName);
  if (!concern || !dt) return true;
  return (CONCERN_DOCTYPES[concern] || []).includes(dt);
}

// Classify a free-text ask at SOURCE (v1_728). An officer/reviewer/model ask can
// be phrased as a document request ("Please provide the PSC register", "a Revolut
// statement") yet arrive through the question channel, so it lands typed
// 'question'. type drives the client checklist, reconciliation and possible-match
// downstream, so detect a document-shaped ask and type it 'document'. docTypeOf()
// is the SAME classifier possible-match uses, so a label it recognises is exactly
// one a document can satisfy; DOC_ASK_RE adds explicit "provide/send/upload a
// [document noun]" phrasing for documents docTypeOf does not enumerate.
const PROVIDE_VERB = /\b(provide|send|share|upload|attach|forward|furnish|supply)\b/i;
const DOC_NOUN = /\b(document|documents|copy|copies|statement|statements|register|certificate|certificates|filing|filings|extract|extracts|report|reports|letter|letters|agreement|agreements|licen[cs]e|passport|invoice|invoices|accounts|declaration|declarations|form|forms|proof|evidence|bill|bills|contract|contracts|prospectus|mandate|resolution|deed|deeds|payslip|lease)\b/i;
const DOC_ASK_RE = new RegExp(PROVIDE_VERB.source + '[\\s\\S]{0,60}' + DOC_NOUN.source, 'i');
// A confirm/clarify/explain/what/why/how question that merely MENTIONS a document
// (e.g. "confirm the currency (the order form and site differ)") is still a
// question, not a document ask. Only vetoes when the label LEADS with a question
// stem, so a genuine "provide evidence of how X was funded" is not vetoed by a
// mid-sentence "how".
const Q_START = /^\s*(please\s+)?(confirm|clarify|explain|describe|comment|tell\s+us|what|why|how|who|whom|which|whether|when|where|do\s+you|are\s+you|is\s+there|could\s+you(\s+please)?\s+(confirm|clarify|explain|tell|describe|comment)|can\s+you\s+(confirm|clarify|explain|tell|describe))\b/i;
export function looksLikeDocumentAsk(label) {
  const s = String(label || '').trim();
  // Explicit provision ask ("provide/send/upload ... a <document>") — a document.
  if (PROVIDE_VERB.test(s)) return DOC_ASK_RE.test(s) || !!docTypeOf(s);
  // A leading question stem means the primary intent is a question, even if a
  // document noun appears later in the sentence.
  if (Q_START.test(s)) return false;
  // Otherwise a bare document-noun ask ("Register of members") is a document.
  return !!docTypeOf(s);
}
// Upgrade only a declared 'question' whose wording names a document. Identity /
// email asks (theme 'identity') are never upgraded: a direct-email ask is not a
// client-uploadable document, and mis-typing it would break the identity flow.
export function inferAskType(label, declaredType = 'question', theme = null) {
  if (declaredType !== 'question') return declaredType;
  if (theme === 'identity') return declaredType;
  return looksLikeDocumentAsk(label) ? 'document' : declaredType;
}

// Build candidate items from the sources Draft Client Request already
// draws on. Each source is optional. Returns NEW candidates only; use
// mergeItems() to fold them into the stored list so existing state wins.
export function buildCandidates({ board = {}, clientQuestions = [], reviewerQuestions = [], crossDocChecks = [], vqfGaps = [], summaryItems = [], tier, round = 0, source = 'system' } = {}) {
  // ASSESSMENT PENDING IS SAID, NOT DEFAULTED (model tier pending review,
  // 30/10/2026). With no tier passed and none on the board, this used to fall
  // back to 'Medium' silently, so a case with no assessed tier had its items
  // marked required as if it were Medium. The tier decides only which items
  // are flagged required (the ORDER they are asked in, never whether they are
  // asked, v1_663). While no tier is in force the items are flagged as the
  // highest tier would flag them (nothing a High case needs is left as
  // optional), and each says so (requiredBasis: 'assessment pending'), so the
  // pending state is visible on the item rather than looking like a Medium
  // decision. The next reconcile after the assessment lands builds on the
  // assessed tier.
  // The tier is the caller's (the tier in force, or a labelled preliminary
  // one); board.riskTier is never read here, as it may be the model's proposal.
  const pendingTier = !tier;
  const t = tier || PENDING_REQUIRED_TIER;
  const _push = (item) => (item && pendingTier ? { ...item, requiredBasis: 'assessment pending' } : item);
  const out = [];
  const seen = new Set();
  const push = (raw) => { const item = _push(raw); if (item && !seen.has(item.id)) { seen.add(item.id); out.push(item); } };

  for (const g of (Array.isArray(board.documentGaps) ? board.documentGaps : [])) {
    if (isInternalGap(g)) continue; // v1_689: internal action, not a client document gap
    const label = typeof g === 'string' ? g : (g && (g.documentType || g.description || g.gap)) || '';
    // v1_673: carry the originating gap's tracking key on the item, so the merged
    // Outstanding items panel can reconcile a document gap to its request item
    // EXACTLY, even after Draft Client Request rewords it (text/labelHead no longer
    // match). Additive; only items built from a gap on/after this carry it.
    // Fault 1 (11/10/2026): the client form of the gap, for every client surface
    // (the officer keeps the label as written); see client-copy.js.
    push(newItem({ type: 'document', label, source: 'documentGaps', tier: t, round, extra: { sourceGapKey: gapKeyOf(g), clientLabel: clientGapLabel(label, docTypeOf(label)), clientLabelFor: String(label || '').trim() } }));
  }
  for (const q of (Array.isArray(clientQuestions) ? clientQuestions : [])) {
    push(newItem({ type: 'question', label: typeof q === 'string' ? q : (q && q.question) || '', source: 'clientQuestions', tier: t, round }));
  }
  // v1_872: the outstanding items Alex listed in its closing summary (pre-submit
  // review). Near duplicates of a document gap or question above are left out,
  // so the card does not offer the same thing twice in two wordings.
  // (Either direction: dedupeAsks drops the shorter of a pair, so a summary line
  // that collapses with an existing label, or makes it collapse, is left out.)
  // v1_876: and a summary line that asks for the same thing as an item already
  // listed, compared on what is asked (the head, before its reason), is left out
  // too (walks wf233cc60 and wb305f5eb: "full board composition" beside "full
  // board of directors"; "Register of directors, to confirm..." beside the
  // register of directors gap; the Northfold statement twice).
  for (const q of (Array.isArray(summaryItems) ? summaryItems : [])) {
    const have = out.map(i => i.label);
    if (dedupeAsks([...have, String(q)]).length !== have.length + 1) continue;
    if (have.some(h => sameAskHead(String(q), h))) continue;
    push(newItem({ type: 'question', label: String(q), source: 'interviewSummary', tier: t, round }));
  }
  for (const q of (Array.isArray(reviewerQuestions) ? reviewerQuestions : [])) {
    push(newItem({ type: 'question', label: typeof q === 'string' ? q : (q && q.question) || '', source: 'reviewerQuestions', tier: t, round }));
  }
  for (const c of (Array.isArray(crossDocChecks) ? crossDocChecks : [])) {
    if (c && typeof c === 'object' && c.resolved) continue;
    const label = typeof c === 'string' ? c : (c && (c.clientAsk || c.question || c.description)) || '';
    push(newItem({ type: 'finding', label, source: 'crossDocChecks', tier: t, round }));
  }
  for (const g of (Array.isArray(vqfGaps) ? vqfGaps : [])) {
    const label = typeof g === 'string' ? g : (g && (g.ask || g.label || g.description)) || '';
    push(newItem({ type: 'question', label, source: 'vqfProfile', tier: t, round }));
  }
  // A missing direct email for anyone whose identity must be verified is
  // required at EVERY tier (v1_663 restores this): without one the compliance
  // team cannot send that person their own secure verification link, so identity
  // verification cannot start at ANY tier. It is AML-required, not tier-scalable,
  // so it is asked regardless of tier. (extra.required overrides the tier
  // ordering heuristic so it is put to the client before optional items.)
  for (const p of verificationParties(board)) {
    const role = p.role ? ` (${p.role})` : '';
    const label = `A direct email address for ${p.name}${role} so the compliance team can send them their own secure identity verification link`;
    push(newItem({ type: 'question', label, why: 'Identity verification cannot be started for this person until we have a direct email address for them.', source: 'verificationEmail', tier: t, round, extra: { required: true, theme: 'identity' } }));
  }
  // v1_689: strip internal-action asks from EVERY generated source (a wallet
  // ownership / receiving-address confirmation reaches the client's list as a
  // documentGap, a clientQuestion, or an officer-reworded send alike). The
  // verificationEmail ask is exempt: its label names "identity verification"
  // but it is a genuine client ask (we need the person's email to send them
  // their own IDV link), so it must never be swept up by that keyword.
  return out.filter(i => i && i.label && (i.source === 'verificationEmail' || !isInternalGap(i.label)));
}

// BELT-AND-BRACES DEDUP for the drafted client request (v1_693). The Draft
// Client Request reworder is told to merge items that raise the same underlying
// issue, and the holistic proportionality pass merges overlapping items too —
// but both are model judgement. This is the deterministic backstop for the case
// they miss: two differently-worded asks about the SAME subject (the live
// example: two "name your MLRO" questions, one plainly worded, one dressed up
// with "responsible for suspicious activity reporting and escalation"). It is
// deliberately CONSERVATIVE — it only drops a line whose distinctive content
// words are a near-total SUBSET of another line's, so a genuinely different ask
// (which is never a near-total subset of another single ask) is never merged
// away. The longer, more complete line is the one kept.
const DEDUP_STOP = new Set(['the','a','an','and','or','of','to','for','your','you','we','our','us','please','provide','confirm','send','share','can','could','would','need','require','kindly','this','that','these','those','with','on','in','at','is','are','be','it','they','them','their','so','as','if','any','each','all','from','by','have','has','give','us','one','their','its','into','about','which','who','whom','where','when','what','how']);
function contentWords(s) {
  return new Set(String(s == null ? '' : s).toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(w => w && !DEDUP_STOP.has(w) && (w.length >= 3 || ['ubo','sof','sow','vat','mlro','aml','id','kyc'].includes(w))));
}
// Is `a` a near-total subset of `b`? (≥90% of a's distinctive words are in b,
// and they share at least 2 distinctive words.)
function nearSubset(a, b) {
  if (a.size < 2 || a.size > b.size) return false;
  let shared = 0;
  for (const w of a) if (b.has(w)) shared++;
  return shared >= 2 && (shared / a.size) >= 0.9;
}
// Collapse near-duplicate asks. Input/output are plain strings in order; the
// kept line for each duplicate cluster is the longest (most complete) one. Pure.
export function dedupeAsks(lines) {
  const arr = (Array.isArray(lines) ? lines : []).map(l => String(l || '')).filter(l => l.trim());
  const words = arr.map(contentWords);
  const dropped = new Array(arr.length).fill(false);
  for (let i = 0; i < arr.length; i++) {
    if (dropped[i]) continue;
    for (let j = 0; j < arr.length; j++) {
      if (i === j || dropped[j]) continue;
      // Drop the SHORTER of a subset pair; keep the longer/more complete line.
      const iSubsetOfJ = nearSubset(words[i], words[j]);
      const jSubsetOfI = nearSubset(words[j], words[i]);
      if (iSubsetOfJ && (!jSubsetOfI || arr[i].length <= arr[j].length)) { dropped[i] = true; break; }
      if (jSubsetOfI && (!iSubsetOfJ || arr[j].length < arr[i].length)) { dropped[j] = true; }
    }
  }
  return arr.filter((_, i) => !dropped[i]);
}

// THE SAME ASK IN TWO WORDINGS (v1_876). Alex's closing summary names an item
// in a few words ("Register of directors, to confirm who the directors are");
// the document gap names the same item with a long reason. dedupeAsks compares
// whole lines, so the reasons keep them apart. This compares only what is asked:
// the head of each line, before its reason, with a few words that name the same
// thing made equal (board, director, officer; statement and statements) and
// filler dropped. A summary head matches when at least 85 percent of its words,
// and at least two, are in the other head, and it names nothing (a bank, a
// person, a company) the other does not. Conservative on purpose: "register of
// members" and "register of directors" share one word and stay apart, and a
// Barclays statement never matches a Northfold one. Pure.
const HEAD_CUT = /\s+because\b|,\s*because\b|,\s*to\s|,\s*if\s|,\s*since\b|\s+so that\b|,\s*so\s|,\s*as\s|[.;:](?:\s|$)|\s+to follow\b|\s+\(/i;
const HEAD_SAME = { board: 'directors', director: 'directors', officer: 'directors', officers: 'directors', statements: 'statement', months: 'month', accounts: 'account' };
const HEAD_FILLER = new Set(['confirming', 'confirmation', 'composition', 'copy', 'recent', 'latest', 'current', 'company', 'companys', 'details', 'evidence', 'evidencing', 'document', 'documents', 'there', 'others', 'other', 'beyond', 'yourself', 'also', 'still', 'whether', 'came']);
function askHead(label) {
  const t = String(label == null ? '' : label).trim();
  const m = t.slice(1).search(HEAD_CUT);
  return m < 0 ? t : t.slice(0, m + 1);
}
function headWords(head) {
  const words = new Set(), named = new Set();
  const raw = head.replace(/[\u2018\u2019']/g, '').split(/[^A-Za-z0-9]+/).filter(Boolean);
  raw.forEach((w, i) => {
    const lw = w.toLowerCase();
    if (DEDUP_STOP.has(lw) || HEAD_FILLER.has(lw) || (lw.length < 3 && !['id', 'aml', 'kyc'].includes(lw))) return;
    const k = HEAD_SAME[lw] || lw;
    words.add(k);
    if (/^[A-Z]/.test(w) && !HEAD_SAME[lw]) named.add(k);
  });
  return { words, named };
}
export function sameAskHead(summaryLine, otherLine) {
  const a = headWords(askHead(summaryLine)), b = headWords(askHead(otherLine));
  if (a.words.size < 2) return false;
  let shared = 0;
  for (const w of a.words) if (b.words.has(w)) shared++;
  if (shared < 2 || shared / a.words.size < 0.85) return false;
  for (const n of a.named) if (!b.words.has(n)) return false;
  return true;
}

// TIPPING-OFF GUARD — BENIGN MLRO-CONTACT CARVE-OUT (v1_693). The tipping-off
// guard withholds any client-facing line containing SAR / escalation / fraud /
// report-to-authority language, because disclosing a SAR consideration is the
// criminal tipping-off offence. That guard is a hard safety floor and stays.
// Its one false positive: a legitimate CDD request to IDENTIFY or CONTACT the
// client firm's own MLRO ("please provide the name and email of your MLRO")
// trips it, because describing an MLRO's role uses the words "suspicious
// activity reporting" and "escalation". This predicate re-admits ONLY that
// narrow case, and ONLY when the line carries NONE of the hard, unambiguous
// tipping-off / deliberation tokens — so a line that actually deliberates about
// a SAR, a fraud assessment, escalation TO an authority, or the MLRO's own
// assessment is still withheld. It is deliberately conservative: it never
// re-admits anything that is not plainly a name/email/contact request for the
// MLRO.
const MLRO_MENTION_RE = /\b(mlro|money[\s-]?laundering reporting officer|nominated officer)\b/i;
const MLRO_CONTACT_RE = /\b(name|e-?mail|email|contact|who\s+(?:is|are)|appointed|details?|identit)\b/i;
// Hard tokens: real internal deliberation. If ANY of these is present the line
// is never re-admitted, whatever else it says.
const HARD_TIPPING_RE = /\bSARs?\b|suspicious transaction|tip(?:ping)?[\s-]?off|fraud (?:concern|risk|assessment)|wider fraud|\bfiled?\b|\bfiling\b|whether (?:a|an|to)\b|report(?:ed|ing)? to (?:the )?(?:FCA|NCA|MROS|FIU|regulator|authorities|financial intelligence|law enforcement)|law enforcement|de[\s-]?risk|internal(?:ly)?|(?:mlro'?s?|nominated officer'?s?|our|the) (?:assessment|view|opinion|decision|consideration|deliberation|concern)|consider(?:ing|ed)? (?:a|an|whether)|escalat\w* (?:to|this|it|the (?:matter|case|concern))/i;
export function isBenignMlroContactAsk(line) {
  const t = String(line == null ? '' : line);
  if (!MLRO_MENTION_RE.test(t) || !MLRO_CONTACT_RE.test(t)) return false;
  return !HARD_TIPPING_RE.test(t);
}

// Fold candidates into the stored list. Existing items keep their state,
// owner, due date, note, evidence and history; only wording and required
// flag refresh from the candidate. Items no longer produced by any source
// are kept (an officer may have asked them deliberately) unless they are
// still 'open' and were never put to the client, in which case they are
// dropped as noise.
// The head of an item's wording (before the "because" clause), used to match a
// re-ask to the item it is a re-ask of even when the TYPE differs: the interview
// files a document gap as type 'document', Draft Client Request re-asks the same
// thing as officer text of type 'question'. Without this the id hash differs and
// the interview item wrongly resolves while a duplicate appears (v1_640).
export function labelHead(label) {
  return normaliseLabel(label).split(/\b(because|as |since |so that)\b/)[0].trim().slice(0, 90);
}

// TIER NEVER DECIDES WHETHER AN AML ITEM IS ASKED (v1_663). An earlier build
// (v1_662) added a gap-scaling layer that moved "enhanced-measure" document gaps
// (source of wealth, audited accounts, ownership tracing) to an officer-only
// context bucket on lower-tier cases. That was wrong: every documentGap has
// already passed the DOCUMENT GAPS DISCIPLINE (the six-test proportionality
// filter in df-process), so forensic over-reach is stripped at generation and
// anything surviving is a genuine, proportionate AML item for THIS file. Scaling
// it by tier only demoted legitimate items and pushed them to a later officer
// round. The layer (classifyGapForTier / tierScaleGaps / REGIME_FLOOR) has been
// removed: every gap the discipline produces is asked of the client in the one
// engagement, at every tier. Tier now shapes only the interview's conversational
// depth (src/shared/interview-scope.js), never which documents are requested.

// OFFICER "TO REQUEST" RECONCILES AGAINST WHAT THE INTERVIEW ALREADY ASKED
// (v1_644). The officer's outstanding list is built from report.documentGaps,
// but the pre-submit interview already puts a tier-scaled subset of those gaps
// to the client and tracks each in requestItems (asked_in_interview, promised,
// answered, not_applicable, resolved …). Without reconciliation the officer
// re-counts every gap as a fresh request — "10 to request" when the client was
// told 4 and answered them (Sage Capital AG, staging 15/09/26). This returns
// the gaps that are STILL a fresh request: a gap survives only when no request
// item covers it, or its item is still merely 'open' (identified, never put to
// the client). A gap whose item has been asked, promised, delegated, partially
// or fully answered, marked N/A, resolved or withdrawn is dropped — it is
// already on the tracked list and must not be re-counted. Matched with the same
// fuzzy matcher reconcileItemsToBoard uses (matchGapKey), so a reworded reason
// does not defeat the match. Pure; the dashboard imports it.
export function pendingGaps(documentGaps = [], requestItems = []) {
  // v1_733: match a gap to existing items with the SAME fuzzy matcher
  // reconcileItemsToBoard (v1_698) uses (matchGapKey), not an exact labelHead.
  // The reprocess rewords a gap between assemblies, so an exact head compare let
  // a REWORDED version of an item the client already actioned (answered, resolved,
  // withdrawn, promised, delegated, not-applicable) resurface as a fresh ask and
  // pinned the stage to Gather with a phantom "to send" count (CompliantID Ltd,
  // 22/09/26). Matching includes resolved/withdrawn items so a reworded-of-done
  // gap reconciles away; a gap matching NO handled item is genuinely new and
  // stays pending (and reconcileItemsToBoard adds it as an 'open' item, so it
  // surfaces in "Required, not yet asked"). Items still merely 'open' (identified,
  // never put to the client) do NOT cover a gap — that gap is still to-send.
  const pool = (Array.isArray(requestItems) ? requestItems : [])
    .filter(it => it && it.label && it.state !== 'open')
    .map(it => ({ key: it.id, label: it.label }));
  return (Array.isArray(documentGaps) ? documentGaps : []).filter(g => {
    if (isInternalGap(g)) return false; // v1_689: internal action, never a client-facing gap
    const label = typeof g === 'string' ? g : (g && (g.documentType || g.description || g.gap)) || '';
    if (!label) return false;
    return !matchGapByAsk(label, pool, 0.4);
  });
}

// DECLINED ITEMS LEAVE ALEX'S "STILL TO COME" (approved 13/10/2026). An item the
// client marked "Can't provide / not relevant" (not_applicable) stays on the
// officer's list for a decision, but Alex must stop naming it as still to come:
// the board's documentGaps are never rewritten by a decline, so the brief, the
// collected data and the points to cover kept listing it next to the ALREADY
// HANDLED line (walk wc7bed076). These filter Alex's VIEW only; the stored
// board, the request items and the dashboard are never changed. Scope is
// not_applicable alone. An item the officer marks still needed leaves that
// state, so it comes back on the next turn.
const declinedItems = (requestItems) => (Array.isArray(requestItems) ? requestItems : []).filter(it => it && it.label && it.state === 'not_applicable');
export function hasDeclinedItems(requestItems = []) { return declinedItems(requestItems).length > 0; }
// The gaps with every gap a declined item covers removed (the same matcher and
// threshold pendingGaps uses).
export function gapsWithoutDeclined(documentGaps = [], requestItems = []) {
  const gaps = Array.isArray(documentGaps) ? documentGaps : [];
  const pool = declinedItems(requestItems).map(it => ({ key: it.id || it.label, label: it.label }));
  if (!pool.length) return gaps;
  return gaps.filter(g => { const label = gapLabelOf(g); return !label || !matchGapByAsk(label, pool, 0.4); });
}
// The points to cover with every point a declined item handles removed
// (followupPointHandled, restricted to the declined items).
export function pointsWithoutDeclined(points = [], requestItems = []) {
  const list = Array.isArray(points) ? points : [];
  const declined = declinedItems(requestItems);
  if (!declined.length) return list;
  return list.filter(q => !(typeof q === 'string' && followupPointHandled(q, declined)));
}

// The client-facing label / tracking key of a document gap (string or object).
// The KEY carries a JSON fallback so an odd-shaped gap still keys uniquely for
// tracking; the LABEL carries an empty fallback so an unlabelled gap is dropped
// from what is put to the client. Both mirror exactly what Draft Client Request
// and the Document Gaps panel used inline before v1_673.
export function gapKeyOf(gap) {
  return typeof gap === 'string' ? gap : ((gap && (gap.documentType || gap.description || gap.gap)) || JSON.stringify(gap));
}
export function gapLabelOf(gap) {
  return typeof gap === 'string' ? gap : ((gap && (gap.documentType || gap.description || gap.gap)) || '');
}

// THE SINGLE SOURCE (v1_673). The document gaps still to put to the client:
// those whose per-gap tracking still reads 'outstanding' AND that no request
// item already covers (pendingGaps). Draft Client Request and the merged
// Outstanding items panel both read the "to request" set from here, so it is
// derived in exactly one place. trackedStatusOf(key) returns the docTracking
// status for a gap key (default 'outstanding' when untracked, matching the
// dashboard's trackedStatus). Reproduces, byte for byte, the inline expression
// prepareFile and computeCaseViewV2Model used before the extraction — pinned by
// tools/harness-outstanding-repoint.mjs against the real Sage case.
export function gapsToRequest(documentGaps = [], trackedStatusOf = () => 'outstanding', requestItems = []) {
  const outstandingLabels = (Array.isArray(documentGaps) ? documentGaps : [])
    .filter(g => trackedStatusOf(gapKeyOf(g)) === 'outstanding')
    .map(gapLabelOf)
    .filter(Boolean);
  return pendingGaps(outstandingLabels, requestItems);
}

export function mergeItems(existing = [], candidates = [], { keepUnseenOpen = false } = {}) {
  const byId = new Map();
  const byHead = new Map();
  for (const e of (Array.isArray(existing) ? existing : [])) {
    if (!e || !e.id) continue;
    byId.set(e.id, { ...e });
    const h = labelHead(e.label); if (h && !byHead.has(h)) byHead.set(h, e.id);
  }
  const seenNow = new Set();
  for (let c of (Array.isArray(candidates) ? candidates : [])) {
    if (!c || !c.id) continue;
    if (!byId.has(c.id)) {
      const h = labelHead(c.label);
      const sameAsk = h && byHead.get(h);
      if (sameAsk) c = { ...c, id: sameAsk, type: byId.get(sameAsk).type || c.type }; // same ask, keep the existing record
    }
    seenNow.add(c.id);
    const cur = byId.get(c.id);
    if (cur) {
      const next = { ...cur, label: c.label || cur.label, why: c.why || cur.why, theme: cur.theme || c.theme, required: cur.requiredOverride != null ? cur.required : c.required };
      // The pending basis follows the candidate: once a rebuild runs on an assessed tier it is gone (30/10/2026).
      if (cur.requiredOverride == null) { if (c.requiredBasis) next.requiredBasis = c.requiredBasis; else delete next.requiredBasis; }
      byId.set(c.id, next);
    } else {
      byId.set(c.id, c);
    }
  }
  // A rebuild passes the whole candidate set, so an open, never asked item it no
  // longer produces is stale and dropped. A round send passes only the drafted
  // lines (v1_840): there an unseen open item is one the draft left out, and it
  // must stay so the send closes it on the record (keepUnseenOpen).
  return [...byId.values()].filter(i => keepUnseenOpen || !(i.state === 'open' && !i.askedAt && !seenNow.has(i.id)));
}

// DELEGATE GROUPS (v1_681). Multiple items handed to ONE delegate in a single
// assignment share ONE owner.token, so the delegate gets one link/one email
// covering the set. These helpers make the token the unit for the delegate
// route, the reminder engine and the harness. A group of one is the pre-v1_681
// per-item case, so old delegations behave unchanged.
//
// Every item currently owned by the delegate token (any state — answered items
// stay in the group as 'done', a declined item reverts owner to the client and
// so drops out). Used by the delegate page to render the whole set with progress.
export function delegateGroupItems(items, token) {
  const t = String(token || '');
  if (!t) return [];
  return (Array.isArray(items) ? items : []).filter(i => i && i.owner && i.owner.kind === 'delegate' && i.owner.token === t);
}
// The still-open delegate groups, keyed by token: { token, owner, items } where
// items are the ones still 'delegated'. Used by the reminder engine to chase ONE
// email per delegate per run, listing that delegate's open items.
export function groupDelegatesByToken(items) {
  const groups = new Map();
  for (const i of (Array.isArray(items) ? items : [])) {
    if (!i || i.state !== 'delegated' || !i.owner || i.owner.kind !== 'delegate' || !i.owner.token) continue;
    const key = i.owner.token;
    if (!groups.has(key)) groups.set(key, { token: key, owner: i.owner, items: [] });
    groups.get(key).items.push(i);
  }
  return [...groups.values()];
}

// States in which an item is still outstanding with the client and so eligible to
// carry a delegation suggestion prompt.
const DELEGATION_OUTSTANDING = new Set(['requested', 'promised', 'partial', 'asked_in_interview', 'open']);

// Interview auto-flag (v1_686). When the client says in the interview that a third
// party holds an outstanding item ("my accountant handles that", "our lawyer has
// the register"), stamp delegationRequested on the matching request item so the
// checklist surfaces the "pass to someone else" prompt — BECAUSE THE CLIENT TOLD
// US. This ONLY surfaces a prompt: it never changes an item's state, never clears
// or resolves anything, and never emails. The client still names and authorises.
//
// `hints` are short free-text mentions extracted from the transcript; each is
// linked to a request item with the EXISTING matcher (matchGapKey — token overlap,
// threshold, tie → no match), so an ambiguous mention never mislabels. Only
// outstanding items are eligible; an already-flagged item is left as-is. Pure;
// returns the (possibly) updated list plus the ids newly flagged.
export function applyDelegationHints(items, hints, { at } = {}) {
  const list = Array.isArray(items) ? items : [];
  const hs = (Array.isArray(hints) ? hints : []).map(h => String(h || '').trim()).filter(Boolean);
  if (!hs.length) return { items: list, flagged: [] };
  const pool = list.filter(i => i && i.label && DELEGATION_OUTSTANDING.has(i.state)).map(i => ({ key: i.id, label: i.label }));
  if (!pool.length) return { items: list, flagged: [] };
  const flag = new Set();
  for (const h of hs) {
    const m = matchGapKey(h, pool);
    if (m && m.key) flag.add(String(m.key));
  }
  if (!flag.size) return { items: list, flagged: [] };
  const stamp = { by: 'interview', at: at || nowIso(), source: 'interview' };
  const next = list.map(i => (i && flag.has(String(i.id)) && !i.delegationRequested) ? { ...i, delegationRequested: stamp } : i);
  return { items: next, flagged: [...flag] };
}

export function transition(item, to, { by = 'system', note, dueAt, owner, evidence } = {}) {
  if (!item || !ITEM_STATES.includes(to)) return item;
  const at = nowIso();
  const next = { ...item, state: to, lastActionAt: at, lastActionBy: by, history: [...(item.history || []), { at, from: item.state, to, by }] };
  if (note != null) next.note = String(note);
  if (dueAt !== undefined) next.dueAt = dueAt;
  if (owner) next.owner = owner;
  if (evidence !== undefined) next.evidence = evidence;
  if (to === 'asked_in_interview' || to === 'requested') next.askedAt = next.askedAt || at;
  if (to === 'answered' || to === 'not_applicable') next.answeredAt = at;
  return next;
}

export function openItems(items = []) {
  return (Array.isArray(items) ? items : []).filter(i => i && OPEN_STATES.includes(i.state));
}

// v1_751: the proportionality flags still waiting for the officer's Withdraw or
// Keep. ONE count for every surface (the request panel, the Draft client request
// gate and the Next action), and only OPEN items count: a withdrawn or resolved
// item's flag is moot. The Next action once counted every item, so a flag whose
// item had been withdrawn stayed "undecided" forever and the case sat on "Decide
// 1 flagged request" with nothing left to decide (end to end walk, run 4).
export function undecidedProportionalityFlags(items = []) {
  return openItems(items).filter(i => i.proportionalityCut && !i.proportionalityCut.decided);
}

// v1_752: 'partial' means two things. Check responses grades a client's reply as
// partial (by: 'check-responses'): the client has responded and the OFFICER now
// decides, Accept or More needed. The officer's own More needed also leaves the
// item partial: that one is back with the CLIENT. Only the first awaits review.
// The panel put every partial under "Already asked, awaiting client" (no Accept,
// no More needed) while the Next action counted every partial as "Review N client
// answers", so the officer was sent to a panel with nothing to click (end to end
// walk, runs 3 and 6). Both now read these predicates.
export function isClientPartial(item) {
  return !!item && item.state === 'partial' && item.lastActionBy === 'check-responses';
}
// v1_812: the round's answered, partial and outstanding counts, from the live
// items, the source the "Awaiting client" header (caseHolder) reads. The counts
// read the snapshot the last Check responses run saved (mlroResponseCheck
// .statuses), which is written back to items only on an exact question match and
// never refreshed after an Accept, a withdrawal or a reconcile, so the header
// said "Nothing outstanding from the client" beside "0 answered, 1 partial, 2
// outstanding" (Fig Markets, production 25/09/26). Only items put to the client
// count ('open' is not yet asked; withdrawn is gone). Partial and outstanding are
// open states, so when the header says nothing is outstanding both are zero.
export function roundCounts(items = []) {
  const asked = (Array.isArray(items) ? items : []).filter(i => i && i.state && i.state !== 'open' && i.state !== 'withdrawn');
  return {
    // v1_900: a client's explanation (not_applicable) is not an answer; it is
    // counted on its own until the officer accepts it or asks again.
    answered: asked.filter(i => ['answered', 'resolved'].includes(i.state)).length,
    explained: asked.filter(i => i.state === 'not_applicable').length,
    partial: asked.filter(i => i.state === 'partial').length,
    outstanding: asked.filter(i => ['requested', 'promised', 'delegated', 'asked_in_interview'].includes(i.state)).length,
    total: asked.length,
  };
}
export function awaitsOfficerReview(item) {
  return !!item && (item.state === 'answered' || item.state === 'not_applicable' || isClientPartial(item));
}

// v1_751: Withdraw on a proportionality flag is a decision, recorded on the flag
// the way Keep is, and the item leaves the request in the same step.
export function withdrawProportionalityCut(item, { by = 'MLRO', at = nowIso(), note = 'Proportionality review: officer confirmed removal as disproportionate' } = {}) {
  if (!item) return item;
  const decidedItem = { ...item, proportionalityCut: { ...(item.proportionalityCut || {}), decided: 'withdraw', decidedBy: by, decidedAt: at } };
  return transition(decidedItem, 'withdrawn', { by, note });
}

// v1_730: keep Alex's MLRO follow-up list in step with the checklist. A follow-up
// point (a raw officer question string held in collected_data.pendingMlroQuestions)
// is "handled" once the client has actioned the matching requestItem — answered or
// uploaded on the checklist, promised, delegated, explained as not applicable, or
// reconciled/resolved. A handled point must drop off Alex's "work through" list, or
// Alex re-asks it and presses a settled point (both against this week's rules).
// Match by token overlap on the item label (the requestItem was created from the
// question, so wording corresponds); the same threshold style as matchGapKey.
const FOLLOWUP_HANDLED_STATES = new Set(['answered', 'promised', 'delegated', 'not_applicable', 'resolved']);
function followupTokens(s) { return new Set(normaliseLabel(s).split(' ').filter(w => w.length > 3)); }
export function followupPointHandled(question, requestItems = [], threshold = 0.6) {
  const qt = followupTokens(question);
  if (!qt.size) return false;
  return (Array.isArray(requestItems) ? requestItems : []).some(it => {
    if (!it || !FOLLOWUP_HANDLED_STATES.has(it.state)) return false;
    const bt = followupTokens(it.label);
    if (!bt.size) return false;
    let n = 0; qt.forEach(w => { if (bt.has(w)) n++; });
    return n / Math.min(qt.size, bt.size) >= threshold;
  });
}
// The follow-up points still genuinely open — pendingMlroQuestions minus the ones
// a matching requestItem shows as handled. Drives what Alex is told to work through.
export function openFollowupPoints(pendingQuestions = [], requestItems = []) {
  return (Array.isArray(pendingQuestions) ? pendingQuestions : []).filter(q => q && !followupPointHandled(q, requestItems));
}

// What Alex asks before submission. EVERY open item is asked in the engagement,
// at every tier — tier NEVER decides whether an AML item is asked (v1_663).
// The tier/required flag and theme order set only the ORDER in which items are
// put to the client (required-first, then by theme), so the most important asks
// come first; the cap is a pure pacing limit on how many go in one close, and it
// is tier-invariant. `deferred` is only the overflow beyond the cap (which stays
// open and is picked up by the same request pipeline), never a tier-based
// exclusion. Two files with identical gaps get an identical ask set whatever
// their tier.
export function selectForInterview(items = [], { tier = 'Medium', cap = 7 } = {}) {
  const open = openItems(items).filter(i => i.state === 'open');
  const themeOrder = (i) => (THEMES[i.theme] || THEMES.other).order;
  // Order only: required-first (so the key asks lead), then methodology theme
  // order. Membership of `ask` does not depend on `required` or on the tier.
  const sorted = [...open].sort((a, b) => (Number(b.required) - Number(a.required)) || (themeOrder(a) - themeOrder(b)));
  const capN = Math.max(1, Number(cap) || 7);
  const ask = sorted.slice(0, capN);
  const deferred = sorted.slice(capN);
  return { ask, deferred, tier };
}

export function groupByTheme(items = []) {
  const groups = {};
  for (const i of (Array.isArray(items) ? items : [])) {
    const k = i.theme || 'other';
    (groups[k] = groups[k] || []).push(i);
  }
  return Object.keys(groups)
    .sort((a, b) => (THEMES[a] || THEMES.other).order - (THEMES[b] || THEMES.other).order)
    .map(k => ({ theme: k, label: (THEMES[k] || THEMES.other).label, items: groups[k] }));
}

// Render the in-interview instruction for Alex from grouped items. Kept
// here so the wording is one place. Alex asks ONE theme at a time.
export function interviewInstruction(ask = []) {
  if (!ask.length) return '';
  const groups = groupByTheme(ask);
  const lines = groups.map(g => `${g.label}: ` + g.items.map(i => i.label).join(' | '));
  return '[SYSTEM: Before the client submits, the file is missing the following, grouped by topic. These are the points that would otherwise come back as a follow-up request AFTER submission and delay the client. Work through them ONE TOPIC AT A TIME, warmly, explaining briefly that you are closing out the last points so their file goes to the compliance team complete. For documents, invite the client to upload them right now in this conversation; if they do not have one to hand, ask by when they can send it and accept a date, or accept "this does not apply to us" with a one-line reason. Never block submission and never repeat anything already provided.\n'
    + lines.join('\n')
    + '\nWhen every topic is covered, briefly confirm what was added, then re-invite the client to type SUBMIT with the standard bold SUBMIT line as the very last line.]';
}

// v1_870: has a request gone out to the client? The one test every surface uses:
// a request was sent (mlroRequestSentAt), or a round's questions are on the case
// (mlroRequestedQuestions, the open round). Next action, the pre request count,
// the request panel, the pipeline, the handover note, the metrics and the
// officer digest all read it here, so none computes it on its own.
export function requestHasGoneOut(collected) {
  const cd = (collected && typeof collected === 'object') ? collected : {};
  return !!cd.mlroRequestSentAt || (Array.isArray(cd.mlroRequestedQuestions) && cd.mlroRequestedQuestions.length > 0);
}

// Who is the case waiting on right now. Deterministic from item state so
// the pipeline can trust it. Officer holds the case when nothing is open
// on the client side.
// v1_870: once a request has gone out (requestSent, from requestHasGoneOut), an item still 'open' was
// raised after it and is not yet asked: it waits on the officer to send it, not
// on the client. The header read "Waiting on client, 2 items" beside Next action's
// "Waiting on the client for 1 item" (walk w3551d4ec, A31). Before the first
// request both count the items to send together, and still do.
export function caseHolder(items = [], { status, requestSent = false } = {}) {
  // v1_921c: after a request has gone out, an item only the interview asked is
  // not with the client (the client sees only what an officer round sent).
  const open = openItems(items).filter(i => !(requestSent && (i.state === 'open' || !sentByOfficer(i))));
  if (!open.length) return { kind: 'officer', label: 'Compliance team', since: null, count: 0 };
  const delegated = open.filter(i => i.state === 'delegated' && i.owner && i.owner.kind === 'delegate');
  // A 'delegated' item with no delegate owner is malformed; it is the client's to resolve (v1_640).
  const clientSide = open.filter(i => !(i.state === 'delegated' && i.owner && i.owner.kind === 'delegate'));
  const oldest = (arr) => arr.reduce((m, i) => (!m || (i.askedAt || i.lastActionAt) < (m.askedAt || m.lastActionAt)) ? i : m, null);
  if (clientSide.length) {
    const o = oldest(clientSide);
    return { kind: 'client', label: 'Client', since: o ? (o.askedAt || o.lastActionAt) : null, count: clientSide.length, delegatedCount: delegated.length };
  }
  const o = oldest(delegated);
  const names = [...new Set(delegated.map(i => i.owner && i.owner.name).filter(Boolean))];
  return { kind: 'delegate', label: names.length ? names.join(', ') : 'Third party', since: o ? (o.askedAt || o.lastActionAt) : null, count: delegated.length, delegatedCount: delegated.length };
}

// v1_980 (walk w21d8919c, A31): before the first request goes out, the Awaiting
// client header counts what Next action counts as "items to request": the open
// request items, or, on a case that has none yet, the document gaps still to
// request; plus the questions the firm asks every client that are unanswered.
// The header added the firm questions (v1_836c) but never the gaps, so a case
// whose pre submission check stored no items (the interview raised nothing
// further) read "Waiting on client, 1 item" (the firm question) beside Next
// action's "6 items to request". After a request has gone out, unchanged.
export function preRequestHolder(holder, { requestSent = false, hasItems = false, gapsToRequest = 0, firmQuestionsOpen = 0 } = {}) {
  const h = holder || { kind: 'officer', label: 'Compliance team', since: null, count: 0 };
  if (requestSent) return h;
  const extra = (hasItems ? 0 : (Number(gapsToRequest) || 0)) + (Number(firmQuestionsOpen) || 0);
  if (!extra) return h;
  return h.kind === 'officer' ? { ...h, kind: 'client', label: 'client', count: extra, since: null } : { ...h, count: h.count + extra };
}

// Promised items whose date has passed and which have not been chased
// since that date.
export function overduePromises(items = [], now = new Date()) {
  const n = now instanceof Date ? now : new Date(now);
  return openItems(items).filter(i => i.state === 'promised' && i.dueAt && new Date(i.dueAt) < n && !(i.promiseChasedAt && new Date(i.promiseChasedAt) >= new Date(i.dueAt)));
}

// Convert a legacy string round (mlroRequestedQuestions) into items so
// cases already in flight on deploy are covered by item-level reminders.
export function itemsFromLegacyRound(cd = {}) {
  const asked = Array.isArray(cd.mlroRequestedQuestions) ? cd.mlroRequestedQuestions : [];
  const round = Number(cd.mlroRequestRounds) || 1;
  const sentAt = cd.mlroRequestSentAt || null;
  const statuses = (cd.mlroResponseCheck && Array.isArray(cd.mlroResponseCheck.statuses)) ? cd.mlroResponseCheck.statuses : [];
  return asked.map((q, i) => {
    const it = newItem({ type: 'question', label: q, source: 'officer', round, state: 'requested' });
    if (!it) return null;
    it.askedAt = sentAt;
    const st = (statuses[i] && String(statuses[i].status || '').toLowerCase()) || '';
    if (st === 'answered') return transition(it, 'answered', { by: 'check-responses', note: statuses[i].note || '' });
    if (st === 'partial') return transition(it, 'partial', { by: 'check-responses', note: statuses[i].note || '' });
    return it;
  }).filter(Boolean);
}

// The short row label (v1_673): the request text up to its "because" clause,
// with a leading "Please provide" / "Could you…" stripped and capped. The full
// text stays as the row's expandable reasoning. Pure; the dashboard imports it.
export function shortRequestLabel(text, max = 80) {
  // Cut the reason clause first, THEN strip a short polite lead-in — so the ask
  // itself is never eaten (a greedy lead-strip once kept "because…" and dropped
  // "Could you confirm whether…").
  let s = String(text || '').trim().split(/\s*(?:—|,?\s+because\b|,?\s+so that\b|;\s)/i)[0].trim();
  s = s.replace(/^(please\s+provide|could you(\s+please)?|we\s+(need|require)|please)\b[:\s,-]*/i, '').trim();
  if (!s) s = String(text || '').trim();
  if (s.length > max) s = s.slice(0, max - 1).replace(/\s+\S*$/, '').trim() + '…';
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

// Match a reworded request line back to the document gap it covers (v1_673). The
// officer's Draft send is model-reworded prose, so a sent line does not map 1:1
// to a gap by text — but the distinctive nouns survive (SoW, AML policy, Sygnum,
// dissolution). This scores each candidate gap by overlap of distinctive tokens
// (common request words like "please/provide/signed/dated/copy" are stopped so
// matching turns on the document itself) and returns the best gap above a
// threshold, else null. Used at send to carry the gap's sourceGapKey onto the
// sent item; pure, so the send flow is harnessed end to end.
const GAP_MATCH_STOP = new Set(['the','a','an','of','and','or','to','for','your','you','we','us','our','is','are','was','were','be','been','on','in','at','it','that','this','these','those','with','from','so','can','could','would','please','provide','provided','copy','copies','signed','dated','date','because','need','needed','require','required','confirm','confirmation','send','give','share','clarify','tell','document','documents','version','file','files','showing','show','as','by','their','they','which','what','who','whom','has','have','had','not','no','yet','still','one','two','per','each','any','all']);
function gapTokens(s) {
  const out = new Set();
  // v1_899: an amount is one word ("400,000" is 400000). Split at the comma,
  // every "x,000" shared "000" and any two amounts in one currency matched.
  for (const w of String(s || '').toLowerCase().replace(/(\d)[,.](?=\d{3}\b)/g, '$1').replace(/(\d)[,.](?=\d{3}\b)/g, '$1').replace(/[^a-z0-9 ]/g, ' ').split(/\s+/)) {
    if (w.length > 2 && !GAP_MATCH_STOP.has(w)) out.add(w);
  }
  return out;
}
// THE HOUSE POLICY FLOOR'S MATCH (v1_815). Guard 0 keeps a request item the
// firm's own policy names. It matched with matchGapKey at 0.4, an overlap
// coefficient over the SMALLER token set, so a short request label sharing one
// word with a floor document was protected ("Shareholders agreement" by
// "Register of shareholders or members"), and a tie between two floor entries
// protected nothing. The rule now (approved 25/09/26): a request label is
// protected by a floor document on an exact label match (case, spacing and
// punctuation aside), or on two shared words AND the overlap score the floor
// always used (0.4 over the smaller token set), so the rule only ever narrows
// what is protected: two generic words shared with a long floor document (a
// bank statement and a structure chart, both saying "company") protect
// nothing new. A label of a single word, on either side, matches only exactly.
// Any floor document that matches protects (a tie is not ambiguity here: both
// entries protect). Returns the matching floor document, or null.
export const FLOOR_MIN_OVERLAP = 0.4;
const floorNorm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
export function floorProtects(label, floorDocs = []) {
  const ln = floorNorm(label);
  if (!ln) return null;
  const lt = gapTokens(label);
  let twoWord = null;
  for (const d of (Array.isArray(floorDocs) ? floorDocs : [])) {
    const dn = floorNorm(d);
    if (!dn) continue;
    if (dn === ln) return String(d);
    if (twoWord) continue;
    const gt = gapTokens(d);
    if (lt.size <= 1 || gt.size <= 1) continue;
    let shared = 0; for (const w of gt) if (lt.has(w)) shared++;
    if (shared >= 2 && shared / Math.min(lt.size, gt.size) >= FLOOR_MIN_OVERLAP) twoWord = String(d);
  }
  return twoWord;
}
// For the harness and the walk: the words a label shares with a floor document.
export function floorSharedWords(label, doc) {
  const lt = gapTokens(label), gt = gapTokens(doc);
  return [...gt].filter(w => lt.has(w));
}

// THE SAME ASK, REWORDED WITH A DIFFERENT REASON (v1_793). Each assembly
// writes a fresh reason clause after "because", and the reasons, not the asks,
// then differ: the register of directors the client had declined (and the
// officer accepted) came back reworded, scored 0.35 against the settled item,
// under 0.4, and was listed "Required, not yet asked" again; the drafting pass
// kept dropping it as already answered, so Next action read "Draft client
// request" round after round (walk run 20). The whole label is tried first, as
// before; failing that, the ask parts alone at a stricter 0.6, and only when the
// entities named in each ask (multi word capitalised names) agree, so a register
// for one company never folds into a register for another.
const askPartOf = (label) => String(label || '').split(/\b(?:because|since|as |so that)\b/i)[0].replace(/^\s*please provide:?\s*/i, '');
const askEntities = (label) => new Set((askPartOf(label).match(/\b[A-Z][A-Za-z&']+(?:\s+(?:of\s+|and\s+)?[A-Z][A-Za-z&']+)+/g) || []).map(x => x.toLowerCase()));
export function matchGapByAsk(line, gapPool = [], threshold = 0.4) {
  const whole = matchGapKey(line, gapPool, threshold);
  if (whole) return whole;
  const le = askEntities(line);
  const pool = (Array.isArray(gapPool) ? gapPool : []).filter(g => {
    if (!g || g.key == null) return false;
    const ge = askEntities(g.label);
    if (!le.size || !ge.size) return le.size === ge.size;
    if (le.size !== ge.size) return false;
    for (const e of le) if (!ge.has(e)) return false;
    return true;
  }).map(g => ({ ...g, label: askPartOf(g.label) }));
  return matchGapKey(askPartOf(line), pool, Math.max(threshold, 0.6));
}
// THE ROUND SEND, PLANNED IN ONE PLACE (v1_794). The officer's request is the
// authority on what stays open: a line re-requests the item it rewords, and an
// item asked in the interview (or still open) that no line re-requests is closed
// as not re-requested. The drafting pass chooses the lines, so an item the client
// had promised could be closed without the officer seeing it (walk run 21: the
// FCA acknowledgement the client said he would check for). The send and the draft
// notice both read this plan, so what the notice says will close is what closes.
export function planRoundSend(prevItems = [], lines = [], { tier, round = 1, names = [], stamp = (it) => it, sender = 'MLRO' } = {}) {
  const prev = Array.isArray(prevItems) ? prevItems : [];
  const neu = buildCandidates({ clientQuestions: lines, tier, round, source: 'officer' })
    .map(stamp)
    .map(it => foldRewordedAsk(prev, it, { names }));
  const heads = new Set(neu.map(i => labelHead(i.label)));
  const closed = [];
  // v1_964: source of funds and source of wealth evidence (protected-evidence.js)
  // is never closed by a round that leaves it out. It stays as it was, still
  // owed, and is reported as kept open so the officer sees why.
  const keptOpen = [];
  // v1_840: keepUnseenOpen. mergeItems drops an open, never asked item the
  // candidates do not produce (right for a rebuild, which passes the whole
  // set). Here the candidates are only the drafted lines, so a question the
  // drafting pass left out was deleted at send: neither re-requested nor
  // closed, gone from the Outstanding list and from both draft notices (walk
  // w7b339f6c: the client's date of birth and the FCA authorisation). It now
  // stays, and closes on the record like any item no line re-requests.
  const merged = mergeItems(prev, neu, { keepUnseenOpen: true }).map(i => {
    if (heads.has(labelHead(i.label))) return transition({ ...i, round }, 'requested', { by: sender });
    const closes = (i.state === 'open') || i.state === 'asked_in_interview' || (['promised', 'delegated', 'partial'].includes(i.state) && !sentByOfficer(i));
    if (closes && isProtectedEvidence(i.label)) { keptOpen.push(i); return i; }
    if (i.state === 'open' && !i.askedAt) { closed.push(i); return transition(i, 'resolved', { by: 'draft-request', note: LEFT_OUT_NOTE }); }
    if (i.state === 'asked_in_interview' || i.state === 'open') { closed.push(i); return transition(i, 'resolved', { by: 'draft-request', note: 'Not re-requested by the officer after the interview' }); }
    // v1_921c: an item only the interview put to the client (promised, delegated
    // or partial, never in an officer round) that this round does not re-request
    // closes the same way. From v1_921 the client sees only what an officer round
    // sent, so it would otherwise stay owed where the client cannot see it, and
    // the case would wait on the client for good (walk w1d3b1cdf).
    if (['promised', 'delegated', 'partial'].includes(i.state) && !sentByOfficer(i)) { closed.push(i); return transition(i, 'resolved', { by: 'draft-request', note: 'Not re-requested by the officer after the interview' }); }
    return i;
  });
  return { merged, closed, keptOpen };
}

export { KEPT_OPEN_NOTE };

// v1_964: the record a send keeps of what it kept open. One case note; null when
// nothing was kept open.
export function keptOpenCaseNote(keptOpen = [], { round } = {}) {
  const items = (Array.isArray(keptOpen) ? keptOpen : []).filter(i => i && i.label);
  if (!items.length) return null;
  return `Request round ${round || ''}: ${items.length === 1 ? 'one item was' : items.length + ' items were'} not in the request and kept open (${KEPT_OPEN_NOTE.toLowerCase()}):\n` + items.map(i => '• ' + i.label).join('\n');
}

// v1_840: the note an item left out of a round carries when the send closes it.
export const LEFT_OUT_NOTE = 'Left out of the request by the drafting pass and not put back by the officer';

// v1_840: of the items a round send would close, the ones the analysis raised
// and nobody has asked yet (open, never asked): what the drafting pass left
// out. The draft's "left out" list names them beside the dropped documents.
export function leftOutOfRequest(closed = []) {
  return (Array.isArray(closed) ? closed : []).filter(i => i && i.state === 'open' && !i.askedAt);
}

// v1_840: the record a send keeps of what it closed as left out. One entry in
// collected_data.mlroRequestRoundHistory (the Closed rounds tab and the Audit
// Record's request history read it), and one case note. Null when nothing was
// left out.
export function leftOutRoundEntry(leftOut = [], { round, at, by } = {}) {
  const items = (Array.isArray(leftOut) ? leftOut : []).filter(i => i && i.label);
  if (!items.length) return null;
  return { round: round || null, leftOut: true, closedAt: at || nowIso(), closedBy: by || 'MLRO',
    items: items.map(i => ({ question: i.label, status: 'left out', note: LEFT_OUT_NOTE })) };
}
export function leftOutCaseNote(entry) {
  if (!entry || !(entry.items || []).length) return null;
  const n = entry.items.length;
  return `Request round ${entry.round || ''}: ${n === 1 ? 'one item the analysis raised was' : n + ' items the analysis raised were'} left out of the request and closed when it was sent (${LEFT_OUT_NOTE.toLowerCase()}):\n` + entry.items.map(i => '• ' + i.question).join('\n');
}

// v1_793: bare open items (never asked) that reword an item the client already
// dealt with. They are the same ask again, and every surface leaves them out:
// the board reconcile drops them, the Outstanding rows omit them, and the
// dashboard does not count them towards "Draft client request".
export function redundantOpenItemIds(items = []) {
  const list = (Array.isArray(items) ? items : []).filter(i => i && i.id && i.label);
  const settled = list.filter(i => CLIENT_DONE_STATES.includes(i.state)).map(i => ({ key: i.id, label: i.label }));
  if (!settled.length) return new Set();
  return new Set(list.filter(i => i.state === 'open' && !i.askedAt && matchGapByAsk(i.label, settled, 0.4)).map(i => i.id));
}
export function matchGapKey(line, gapPool = [], threshold = 0.4) {
  const lt = gapTokens(line);
  if (!lt.size) return null;
  let best = null, bestScore = 0, tie = false;
  for (const g of (Array.isArray(gapPool) ? gapPool : [])) {
    if (!g || g.key == null) continue;
    const gt = gapTokens(g.label);
    if (!gt.size) continue;
    let inter = 0; for (const w of gt) if (lt.has(w)) inter++;
    const score = inter / Math.min(lt.size, gt.size); // overlap coefficient
    if (score > bestScore) { bestScore = score; best = g; tie = false; }
    else if (score === bestScore && best && g.key !== best.key) { tie = true; }
  }
  // A clear winner only: a tie between two gaps means the line is ambiguous, and
  // attaching the WRONG gap (mislabelling) is worse than leaving it unmatched
  // (which degrades to a to-request row, no double). Bigram/entity-name matching
  // was tried and dropped — recurring entity names ("Sage Capital Management")
  // produced false matches.
  return (bestScore >= threshold && !tie) ? best : null;
}

// Option B (v1_675): resolve the document gap key(s) for a drafted request line.
// The drafting model now returns each reworded line WITH the id(s) of the gap(s)
// it covers, and prepareFile keeps that as draftGapMap: { <exact line text>: [gap
// key, …] }. An UNEDITED model line is found verbatim in the map and carries its
// key(s) with NO heuristic — the model's own attribution, so a merge (one line,
// several gaps) is honoured exactly. Only a hand-EDITED or hand-ADDED line is
// absent from the map and falls back to the token-overlap matchGapKey (which may
// match one gap, or degrade to no key — a to-request row, never a wrong label).
// Returns an array of keys (0, 1 or many). Pure; harnessed with the send flow.
export function resolveGapKeys(label, draftGapMap = {}, gapPool = []) {
  const exact = draftGapMap && draftGapMap[String(label || '').trim()];
  if (Array.isArray(exact) && exact.length) return [...new Set(exact.map(String))];
  const m = matchGapKey(label, gapPool);
  return m ? [String(m.key)] : [];
}

// Stamp resolved gap key(s) onto a freshly built request item. sourceGapKey
// (singular) stays for back-compat and the exact single-gap attach; sourceGapKeys
// (plural) is added ONLY when a line merged several gaps, so every merged gap
// reconciles onto this one row in outstandingRows — no doubling. No keys → the
// item is returned unchanged (an officer-typed clarification carries no gap).
export function stampGapKeys(item, keys = []) {
  if (!item || !Array.isArray(keys) || !keys.length) return item;
  return keys.length > 1
    ? { ...item, sourceGapKey: keys[0], sourceGapKeys: [...keys] }
    : { ...item, sourceGapKey: keys[0] };
}

// v1_853: WHAT A POSSIBLE MATCH KNOWS. An item built from a document gap
// (buildCandidates stamps sourceGapKey) can be flagged when its gap leaves the
// analysis's list; any other item (a question, a finding, an officer's own
// question) never matched a gap and is never flagged.
export const isDocumentGapItem = (it) => !!(it && (it.sourceGapKey != null || (Array.isArray(it.sourceGapKeys) && it.sourceGapKeys.length) || it.source === 'documentGaps' || it.type === 'document'));

// The gaps this run took off the list, and why, from what the board records:
// the proportionality review's cuts, merges and downgrades, the future
// obligations it deferred, and the gaps the ask reason repair reworded.
export function removedGapsFromBoard(board) {
  const b = board || {};
  const out = [];
  const pc = (b.proportionality && Array.isArray(b.proportionality.changes)) ? b.proportionality.changes : [];
  for (const c of pc) {
    if (!c || !c.before) continue;
    if (c.action === 'cut') out.push({ label: String(c.before), reason: 'removed by the proportionality review' + (c.reason ? ` (${String(c.reason).replace(/[.\s]+$/, '')})` : '') });
    else if (c.action === 'merge') out.push({ label: String(c.before), reason: 'folded into another item by the proportionality review' });
    else if (c.action === 'downgrade') out.push({ label: String(c.before), reason: 'reworded by the proportionality review to ask for lighter evidence' });
  }
  for (const d of (Array.isArray(b.deferredObligations) ? b.deferredObligations : [])) {
    if (d && d.text) out.push({ label: String(d.text), reason: 'deferred as a future obligation, to revisit after the first reporting period' });
  }
  for (const r of ((b.askReasons && Array.isArray(b.askReasons.rewords)) ? b.askReasons.rewords : [])) {
    if (r && r.before) out.push({ label: String(r.before), reason: 'reworded by the analysis, so this item no longer matches its wording' });
  }
  return out;
}

// The recorded reason a gap matching this item left the list, or null.
export function gapLeftReason(label, removedGaps) {
  const pool = (Array.isArray(removedGaps) ? removedGaps : []).filter(g => g && g.label && g.reason).map((g, i) => ({ key: String(i), label: g.label, reason: g.reason }));
  if (!label || !pool.length) return null;
  const m = matchGapKey(label, pool, 0.4);
  return m ? (pool.find(p => p.key === m.key) || {}).reason || null : null;
}

// IS THIS ITEM'S GAP STILL ON THE BOARD, IN ANY WORDING? (v1_899). The board
// rewords gaps on every assembly. The whole label word match (matchGapKey at
// 0.4, a tie counting as no match) missed both Diment items (66f89346): "source
// of the AED 400,000 lien funds" now sits inside "evidence of how the AED
// 400,000 VARA regulatory capital and the AED 100,000 share capital were
// funded", and an accounts item came back as financial statements. Each was
// shown as "Gap removed". This check is for that one question only, so it reads
// more generously than the matcher used to attach items:
//   - amounts are one token ("400,000" is 400000), currency codes and generic
//     request words (evidence, proof, source, how, latest) are dropped;
//   - plain endings are folded (funds, funded, funding are one word),
//     "financial statements" reads as accounts and a year (2025, FY2025, annual)
//     as year;
//   - a shared amount of four or more digits counts as two words;
//   - the ask part of each text is compared, and the gap text the item was made
//     from (sourceGapKey) is tried as well as its current label;
//   - two shared words and half of the shorter text is enough, and a tie between
//     two live gaps counts as PRESENT (the gap is there in some form; only a gap
//     that is genuinely absent is "removed").
const PRESENCE_STOP = new Set(['evidence', 'evidencing', 'proof', 'source', 'how', 'latest', 'recent', 'most', 'current', 'full', 'relevant', 'supporting', 'detail', 'details', 'explanation', 'explain', 'information', 'company', 'aed', 'usd', 'gbp', 'eur', 'chf', 'sgd', 'hkd', 'amount']);
const presenceStem = (w) => {
  if (w.length > 5 && /ing$/.test(w)) return w.slice(0, -3);
  if (w.length > 4 && /ed$/.test(w)) return w.slice(0, -2);
  if (w.length > 4 && /ies$/.test(w)) return w.slice(0, -3) + 'y';
  if (w.length > 3 && /s$/.test(w) && !/ss$/.test(w)) return w.slice(0, -1);
  return w;
};
function presenceTokens(text) {
  const t = askPartOf(text).toLowerCase()
    .replace(/(\d)[,.](?=\d{3}\b)/g, '$1').replace(/(\d)[,.](?=\d{3}\b)/g, '$1')
    .replace(/\bfinancial statements?\b/g, 'accounts')
    .replace(/\b(?:fy\s?)?(?:19|20)\d{2}\b/g, ' year ').replace(/\b(?:annual|yearly|year end|financial year)\b/g, ' year ');
  const out = new Set();
  for (const raw of t.replace(/[^a-z0-9 ]/g, ' ').split(/\s+/)) {
    if (raw.length <= 2 || GAP_MATCH_STOP.has(raw)) continue;
    const w = presenceStem(raw);
    if (w.length > 2 && !PRESENCE_STOP.has(w) && !PRESENCE_STOP.has(raw)) out.add(w);
  }
  return out;
}
export function gapStillPresent(item, gapPool = []) {
  const texts = [item && item.label, item && item.sourceGapKey, ...((item && Array.isArray(item.sourceGapKeys)) ? item.sourceGapKeys : [])].filter(t => typeof t === 'string' && t.trim());
  const pool = (Array.isArray(gapPool) ? gapPool : []).filter(g => g && g.label);
  for (const text of texts) {
    if (pool.some(g => g.key != null && String(g.key) === text)) return true;
    const lt = presenceTokens(text);
    if (lt.size < 2) continue;
    for (const g of pool) {
      const gt = presenceTokens(g.label);
      if (gt.size < 2) continue;
      // A shared amount of four or more digits names the same money and counts
      // as two words (the lien funds and the regulatory capital are one AED 400,000).
      let inter = 0; for (const w of gt) if (lt.has(w)) inter += /^\d{4,}$/.test(w) ? 2 : 1;
      if (inter >= 2 && inter / Math.min(lt.size, gt.size) >= 0.5) return true;
    }
  }
  return false;
}

// The card the request items panel shows for an item, or null:
//   document: a document line matched (named; View where the upload is linked);
//   gap_left: the gap left the analysis's list with no document matched, with
//     the reason where known;
//   null: nothing to show (decided, no flag, or an item that never matched a
//     document gap).
export function possibleMatchCard(it, liveGaps) {
  const pm = it && it.possibleDocMatch;
  if (!pm || pm.decided || !isDocumentGapItem(it)) return null;
  if (pm.doc) return { kind: 'document', doc: String(pm.doc) };
  // v1_899: "Gap removed" only when the analysis recorded a reason or the gap is
  // genuinely absent from the current board. A flag stored by an earlier run
  // whose gap is still there, reworded, is not shown (the next rebuild clears it).
  if (!pm.reason && Array.isArray(liveGaps)) {
    const pool = liveGaps.map(g => ({ key: gapKeyOf(g), label: gapLabelOf(g) })).filter(g => g.label && !isInternalGap(g.label));
    if (matchGapByAsk(it.label, pool, 0.4) || gapStillPresent(it, pool)) return null;
  }
  return { kind: 'gap_left', reason: pm.reason || null };
}

// RECONCILE a stale requestItems set against a freshly-assembled board's
// documentGaps (v1_698). The documents-first pipeline reassembles the board on
// every run, so requestItems built at pre-submit (from an earlier, thinner
// document view) drift out of step with the full-document board: the officer
// then sees outstanding items for documents the client already provided. This
// reconciles them, WITHOUT re-deriving and duplicating.
//
// The board rewords gaps between assemblies, so items are tied to gaps by FUZZY
// match (matchGapKey), never by exact labelHead — an exact merge would treat a
// reworded gap as a new item and double it. Existing items are the spine:
// state, history, ids and WORDING are preserved (no churn under the client).
//   - an OPEN item still matching a live documentGap → kept unchanged (any stale
//     possibleDocMatch cleared);
//   - an OPEN item whose gap is NO LONGER a live documentGap (the fuller board
//     reconciled it away — a document the client provided likely covers it) →
//     kept OUTSTANDING and flagged with possibleDocMatch for the officer to
//     confirm (naming the received document when confidently matched, else null).
//     Nothing is auto-resolved;
//   - a bare 'open' item never asked, whose gap is gone → dropped as noise
//     (mirrors mergeItems' own drop rule);
//   - a live documentGap not covered by ANY existing item (fuzzy) → ADDED as a
//     new 'open' item, in the board's wording (a genuinely new gap);
//   - CLIENT_DONE items (answered / not_applicable / resolved / withdrawn) and
//     internal-action gaps are never touched.
// Pure and harnessed. `receivedDocs` are document names/types for the match hint.
export function reconcileItemsToBoard(existingItems, documentGaps = [], opts = {}) {
  const items = (Array.isArray(existingItems) ? existingItems : []).filter(Boolean);
  const gaps = (Array.isArray(documentGaps) ? documentGaps : [])
    .filter(g => { const l = gapLabelOf(g); return l && !isInternalGap(l); });
  const at = opts.at || new Date().toISOString();
  const receivedDocs = (Array.isArray(opts.receivedDocs) ? opts.receivedDocs : []).filter(Boolean).map(String);
  const gapPool = gaps.map(g => ({ key: gapKeyOf(g), label: gapLabelOf(g) }));
  const docPool = receivedDocs.map(n => ({ key: n, label: n }));
  const removedGaps = (Array.isArray(opts.removedGaps) ? opts.removedGaps : []).filter(g => g && g.label && g.reason);

  const redundant = redundantOpenItemIds(items);

  // 1. Reconcile existing OPEN items against the current gaps.
  const reconciled = items.map(it => {
    if (!it || !it.label || !OPEN_STATES.includes(it.state)) return it; // done-states untouched
    // v1_699: an internal-action ask (identity/Sumsub, screening, Travel Rule,
    // Form A/K, wallet ownership) that leaked into requestItems is never a
    // client-facing item — drop it here so a reprocess clears any historical leak.
    if (isInternalGap(it.label)) return null;
    // v1_793: a bare open item, never asked, that rewords an item the client
    // already dealt with is the same ask again; drop it (walk run 20's duplicate
    // register of directors kept "Draft client request" alive every round).
    if (redundant.has(it.id)) return null;
    // v1_899: still live if the gap is on the board in any wording (the ask part
    // matcher, then the presence check, which counts a tie as present).
    const live = matchGapByAsk(it.label, gapPool, 0.4) || gapStillPresent(it, gapPool);
    if (live) return it.possibleDocMatch ? { ...it, possibleDocMatch: null } : it; // still a live gap
    // Gap gone from the fuller board:
    if (it.state === 'open' && !it.askedAt) return null; // bare open, never asked → drop noise
    // v1_853: a question or finding never matched a document gap, so a gap
    // leaving the list says nothing about it: no flag (and an undecided flag an
    // earlier run set is cleared). Until v1_853 every asked question was flagged
    // "a document the client uploaded may already address this" on every run.
    if (!isDocumentGapItem(it)) return (it.possibleDocMatch && !it.possibleDocMatch.decided) ? { ...it, possibleDocMatch: null } : it;
    const why = gapLeftReason(it.label, removedGaps);
    if (it.possibleDocMatch) {                           // already flagged, no churn
      const pm = it.possibleDocMatch;
      return (!pm.doc && !pm.decided && !pm.reason && why) ? { ...it, possibleDocMatch: { ...pm, gapLeft: true, reason: why } } : it;
    }
    let dm = docPool.length ? matchGapKey(it.label, docPool, 0.3) : null;
    // v1_720: only NAME a document whose TYPE can answer the item's concern; a
    // word-overlap match to an incompatible type (a memorandum for a funding ask)
    // is dropped to the generic hint rather than named.
    if (dm && !docMatchSatisfiesConcern(it.label, dm.label)) dm = null;
    // v1_853: a document line matched, or the gap simply left the list (with
    // the reason where the run recorded one). Nothing claims a document it has
    // not matched.
    return { ...it, possibleDocMatch: dm ? { doc: dm.label, at } : { doc: null, gapLeft: true, reason: why, at } };
  }).filter(Boolean);

  // 2. Add live gaps not already covered by ANY existing item (fuzzy, so a
  //    reworded gap already tracked is not re-added as a duplicate).
  const itemPool = items.filter(i => i && i.label).map(i => ({ key: i.id, label: i.label }));
  // v1_899: a gap an existing item is still present in (any wording) is covered.
  const uncovered = gaps.filter(g => !matchGapByAsk(gapLabelOf(g), itemPool, 0.4)
    && !items.some(i => i && i.label && gapStillPresent(i, [{ key: gapKeyOf(g), label: gapLabelOf(g) }])));
  const additions = uncovered.length
    ? buildCandidates({ board: { documentGaps: uncovered }, tier: opts.tier, round: opts.round || 0 })
    : [];

  return [...reconciled, ...additions];
}

// APPLY A PROPORTIONALITY VERDICT TO THE REQUEST ITEMS (v1_703). The pass writes
// its result to report.documentGaps + report.proportionality, but requestItems —
// the surface the officer's Outstanding list and Draft Client Request actually
// send from — was never updated, so a downgrade's lightened wording and a cut's
// removal never reached the client-facing item (CompliantID Ltd, 21/09/26).
// This closes that write-back, scoped to proportionality changes only:
//   - DOWNGRADE → relabel the matching item to the lighter wording, automatically
//     (low-risk: it is a rewording of an ask that stays);
//   - CUT / MERGE → NEVER auto-withdraw. Flag the matching item with the pass's
//     reason and leave a Withdraw/Keep decision to the officer (proportionalityCut).
//     The pass proposes; a human disposes.
// Correspondence is fuzzy (the documentGap wording the pass saw and the item's
// pre-submit wording diverge) BUT kind-aligned: a documentGap change only matches
// a document item and a clientQuestion change only a question item — so a cut
// funding QUESTION never touches a surviving statement DOCUMENT. Pure and
// idempotent: an item already relabelled or already flagged/decided is left alone.
const CHANGE_KIND_TO_ITEM_TYPE = { documentGap: 'document', clientQuestion: 'question' };
export function applyProportionalityToRequestItems(requestItems, changes, opts = {}) {
  const items = Array.isArray(requestItems) ? requestItems : [];
  const acted = (Array.isArray(changes) ? changes : []).filter(c => c && c.before && (c.action === 'cut' || c.action === 'merge' || c.action === 'downgrade'));
  // v1_715: SUPERSEDE — `changes` is a COMPLETE fresh verdict from a pass that ran
  // to completion, so an UNDECIDED proportionalityCut the new verdict no longer
  // asserts is STALE and must be cleared (a prior pass flagged it; this pass — or
  // Guard 2, now protecting UBO/control items — does not). Without this, a reprocess
  // left the officer looking at Withdraw/Keep flags from a superseded run
  // (CompliantID Ltd, 22/09/26). An officer's DECIDED flag is never cleared.
  const supersede = !!opts.supersede;
  if (!acted.length && !supersede) return { items, relabeled: 0, flagged: 0, cleared: 0 };
  const at = opts.at || new Date().toISOString();
  const downs = acted.filter(c => c.action === 'downgrade' && c.after);
  const cuts = acted.filter(c => c.action === 'cut' || c.action === 'merge');
  const itemType = (it) => (it && it.type === 'question') ? 'question' : 'document';
  const alignedPool = (list, itype) => list
    .filter(c => (CHANGE_KIND_TO_ITEM_TYPE[c.kind] || 'document') === itype)
    .map(c => ({ key: c.before, label: c.before }));
  let relabeled = 0, flagged = 0, cleared = 0;
  const next = items.map(it => {
    if (!it || !it.label || !OPEN_STATES.includes(it.state)) return it; // only open, client-facing items
    if (it.proportionalityCut && it.proportionalityCut.decided) return it; // an officer's decision is never touched
    const itype = itemType(it);
    let out = it;
    // DOWNGRADE → relabel automatically (unless already lightened by a prior run).
    if (!out.proportionalityLightened) {
      const dPool = alignedPool(downs, itype);
      const dm = dPool.length ? matchGapKey(out.label, dPool, 0.4) : null;
      const dch = dm ? downs.find(c => c.before === dm.key) : null;
      if (dch && dch.after) { relabeled++; out = { ...out, label: dch.after, why: '', proportionalityLightened: { from: out.label, at } }; }
    }
    // CUT / MERGE → FLAG for officer Withdraw/Keep (never auto-withdraw). An already
    // undecided-flagged item is left as-is when the verdict still asserts it; when
    // the verdict no longer asserts it and we are superseding, the stale flag is CLEARED.
    const cPool = alignedPool(cuts, itype);
    const cm = cPool.length ? matchGapKey(out.label, cPool, 0.4) : null;
    const cch = cm ? cuts.find(c => c.before === cm.key) : null;
    if (cch) {
      if (!out.proportionalityCut) { flagged++; out = { ...out, proportionalityCut: { action: cch.action, reason: cch.reason || 'Not proportionate to this case.', at } }; }
    } else if (out.proportionalityCut && supersede) {
      cleared++; const { proportionalityCut, ...rest } = out; out = rest; // stale undecided flag the new verdict dropped
    }
    return out;
  });
  return { items: next, relabeled, flagged, cleared };
}

// THE ONE outstanding list (v1_673). One row per request, spined on requestItems,
// with document gaps reconciled onto them so the same request never appears twice.
// A gap is matched to its item by sourceGapKey (exact, for items built after that
// field landed) or, failing that, by labelHead (best-effort for older items).
// A matched gap attaches to its item's row (so the row can carry the gap's Upload
// / Status controls); an unmatched gap becomes its own "to request" row. Response
// statuses attach to item rows by question labelHead. docStatusOf(key) returns the
// per-gap tracking status. Pure; presentation reconciliation only.
export function outstandingRows({ requestItems = [], documentGaps = [], docStatusOf = () => 'outstanding', responseStatuses = [] } = {}) {
  // v1_793: a never asked rewording of a settled item is not an outstanding row.
  const _redundant = redundantOpenItemIds(requestItems);
  const items = (Array.isArray(requestItems) ? requestItems : []).filter(i => i && i.id && !_redundant.has(i.id));
  const isDoneState = (s) => s === 'resolved' || s === 'withdrawn';
  const bySourceGap = new Map();
  const byHead = new Map();
  for (const it of items) {
    // An item carries either a single sourceGapKey or, when a reworded request
    // line merged several document gaps (Option B, v1_675), sourceGapKeys (plural).
    // Register the item under EVERY key it claims so each merged gap reconciles
    // onto this one row and none spawns a duplicate "to request" row.
    const keys = (Array.isArray(it.sourceGapKeys) && it.sourceGapKeys.length)
      ? it.sourceGapKeys
      : (it.sourceGapKey != null ? [it.sourceGapKey] : []);
    for (const rawKey of keys) {
      // Prefer an ACTIVE item when several items share a gap key: after a Draft
      // send the pre-submit gap item resolves and the live reworded item also
      // carries the key, and the gap must attach to the LIVE row (so Upload sits
      // on it), not the hidden resolved sibling.
      const key = String(rawKey);
      const cur = bySourceGap.get(key);
      if (!cur || (isDoneState(cur.state) && !isDoneState(it.state))) bySourceGap.set(key, it);
    }
    const h = labelHead(it.label); if (h && !byHead.has(h)) byHead.set(h, it);
  }
  const respByHead = new Map();
  for (const rs of (Array.isArray(responseStatuses) ? responseStatuses : [])) {
    const h = labelHead((rs && rs.question) || ''); if (h && !respByHead.has(h)) respByHead.set(h, rs);
  }
  const rows = items.map(it => ({
    kind: 'item', id: it.id, item: it, gap: null, docStatus: null,
    response: respByHead.get(labelHead(it.label)) || null,
  }));
  const rowByItemId = new Map(rows.map(r => [r.item.id, r]));
  for (const g of (Array.isArray(documentGaps) ? documentGaps : [])) {
    if (isInternalGap(g)) continue; // v1_689: internal action, not an officer outstanding row
    const key = gapKeyOf(g);
    const head = labelHead(gapLabelOf(g));
    // v1_793: failing the key and the exact head, the same ask reworded (a new
    // reason clause) attaches to the item that already carries it.
    let matched = bySourceGap.get(String(key)) || (head ? byHead.get(head) : null);
    if (!matched) { const m = matchGapByAsk(gapLabelOf(g), items.map(i => ({ key: i.id, label: i.label })), 0.4); if (m) matched = items.find(i => i.id === m.key) || null; }
    if (matched) {
      const row = rowByItemId.get(matched.id);
      if (row && !row.gap) { row.gap = g; row.docStatus = docStatusOf(key); }
      continue;
    }
    rows.push({ kind: 'gap', id: 'gap:' + key, item: null, gap: g, docStatus: docStatusOf(key), response: null });
  }
  return rows;
}

// v1_777: A REWORDED ASK IS THE SAME ASK. The officer's draft rewords an item
// the client already owes ("A copy of the company's register of directors (or
// the current Companies House officers listing ...)" as "Please provide: the
// register of directors, or the Companies House officers listing, ..."); the
// send merge matched only on the label head, so the promised round 0 item and
// the new round 1 item both stood and the client's checklist asked for the same
// document twice (walk run 15). foldRewordedAsk gives a new officer item the id
// of the one prior item the client still owes whose ASK (the label head, before
// "because") shares most of its words, so mergeItems keeps one record, reworded
// and re-requested. A clear single match only: a tie, or a weak overlap, keeps
// the new item separate (a wrong merge would lose an ask).
const STILL_OWED = ['requested', 'asked_in_interview', 'promised', 'delegated', 'partial', 'open'];
export function foldRewordedAsk(prevItems = [], item, opts = {}) {
  if (!item || !item.label) return item;
  // v1_917: an item the client still owes under this very id IS this ask. Ids
  // come from the label, so a drafted line that repeats an outstanding item's
  // wording arrives with that item's id. Skipping it (p.id === item.id below)
  // and folding the line into the next best match relabelled a different item
  // with the same wording, and the send requested both: the client's checklist
  // listed the accounts twice (walk wd001b904, A17).
  if ((Array.isArray(prevItems) ? prevItems : []).some(p => p && p.id === item.id && STILL_OWED.includes(p.state))) return item;
  const threshold = opts.threshold || 0.6;
  // The parties' names are not the ask: "the register of directors for X" and
  // "the accounts for X" share X and nothing else.
  const nameWords = gapTokens((opts.names || []).join(' '));
  // v1_785: the WHOLE ask (up to because / since / as / so that), not the 90
  // character label head, which clipped both labels mid word and scored a real
  // rewording under the threshold (walk run 17: two Northfold statement items).
  // Request phrasing is not the ask either.
  // A17 (walk w5a88c301, 16/10/2026): the drafted line often gives its reason as
  // a second sentence ("... in April 2023. These show where the company's
  // starting capital came from ..."), with no "because". Those words are not the
  // ask either, and they pulled a real rewording of an item owed from the pre
  // submit review under the bar: the send kept both items, so the client's
  // checklist asked for the same statements twice. The ask is the first sentence.
  // A full stop after a common abbreviation ("Ltd. ", "No. ", "Mr. ", "e.g. ",
  // "St. ") does not end it: it is dropped before the split (owner 16/10/2026).
  // No lookbehind: this file runs in the dashboard, and older Safari cannot parse one.
  const ASK_FILLER = new Set(['recent', 'latest', 'current', 'example', 'please', 'kindly', 'also', 'full', 'most', 'months', 'month']);
  const ABBR_DOT = /\b(ltd|no|nos|mr|mrs|ms|dr|st|co|inc|plc|jr|sr|vs|e\.g|i\.e)\.(?=\s)/g;
  const askPart = (label) => normaliseLabel(label).replace(ABBR_DOT, (m, w) => w.replace(/\./g, '')).split(/\b(because|since|as |so that)\b/)[0].split(/\.\s/)[0].replace(/^please provide:?\s*/, '');
  const askTokens = (label) => { const t = gapTokens(askPart(label)); for (const w of nameWords) t.delete(w); for (const w of ASK_FILLER) t.delete(w); for (const w of ['limited', 'ltd', 'company', 'companies', 'plc']) if (!/companies house/i.test(label) || w !== 'companies') t.delete(w); return t; };
  const nt = askTokens(item.label);
  if (nt.size < 2) return item;
  let best = null, bestScore = 0, tie = false;
  for (const p of (Array.isArray(prevItems) ? prevItems : [])) {
    if (!p || !p.id || p.id === item.id || !STILL_OWED.includes(p.state)) continue;
    const pt = askTokens(p.label);
    if (pt.size < 2) continue;
    let inter = 0; for (const w of pt) if (nt.has(w)) inter++;
    const score = inter / Math.min(nt.size, pt.size);
    // Three shared ask words, or two that are the whole of the shorter ask.
    if (inter < 2 || (inter < 3 && score < 1)) continue;
    if (score > bestScore) { bestScore = score; best = p; tie = false; }
    else if (score === bestScore && best && p.id !== best.id) tie = true;
  }
  if (!best || tie || bestScore < threshold) return item;
  return { ...item, id: best.id, type: best.type || item.type };
}

// WHO WROTE THE TEXT UNDER A REQUEST ITEM (v1_896). The panel showed the
// verdict note from Check responses as "Client response:", and an item's own
// note with no label at all, although that note may be the client's words, a
// delegate's, or the platform's reading (Check responses, or the chat
// reconciler recorded as "client (via Alex)"). On Diment an officer could read
// "No source evidence for AED 400,000 lien funds provided since request." as
// the client's own answer. Each text now names its source. No dashes.
const ukDay = (iso) => { const d = new Date(iso || ''); return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10).split('-').reverse().join('/'); };
export function responseCheckLabel(checkedAt) {
  const day = ukDay(checkedAt);
  return 'Check responses assessment' + (day ? ` (${day})` : '') + ', not the client\'s words';
}
export function itemNoteLabel(item) {
  const by = String((item && item.lastActionBy) || '').toLowerCase();
  if (by === 'client') return 'Client\'s answer';
  if (by === 'delegate') return 'Delegate\'s answer';
  if (by === 'check-responses') return 'Check responses assessment, not the client\'s words';
  if (by === 'client (via alex)') return 'Platform reading of the client\'s chat, not the client\'s words';
  return 'Note';
}
